/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

// Apple-Maps-inspired urban intelligence view for real-estate investors.
// Phase 1: focused MSAs + tracked properties from connected sheet,
// glassmorphism panels, fly-to animations, theme-aware basemap.

const API = 'https://coast-terminal-production.up.railway.app';

interface Props {
  token: string;
  onExitToBoard: () => void;
  onExitToDashboard: () => void;
  onOpenExplorer?: () => void;
}

type Theme = 'light' | 'dark';

interface MarketMetric {
  value: number;
  year: number;
  yoy_pct: number | null;
  series?: Array<{ year: number; value: number }>;
  source?: string;
  unit?: string;
  vintage?: string;
}

// ── Property Intelligence types ─────────────────────────────────────────
// These mirror the /intel/property/{slug} response. They describe a
// single anchored property and everything the spatial-intelligence layer
// pulls about its surrounding 15-minute zone.

interface IntelPOI {
  id:         string;
  lat:        number;
  lng:        number;
  name:       string;
  category:   string;
  ring:       'life' | 'social' | 'work';
  distance_m: number;
}
interface IntelCategoryRow {
  key:      string;
  label:    string;
  color:    string;
  count:    number;
  target:   number;
  coverage: number;
}
interface IntelPersona {
  key:   string;
  label: string;
  tag:   string;
  icon:  string;
  fit:   number;          // 0..100
  wants: string[];        // top-3 category keys
}
interface IntelBundle {
  property: {
    slug: string; name: string; address: string;
    lat: number; lng: number;
    city: string; state: string;
    submarket: string; year_built: number | null;
    asset_type: string;
  };
  rings: Array<{ key: string; label: string; radius_m: number }>;
  categories: Array<{ key: string; label: string; color: string; target: number }>;
  lifestyle: { score: number; categories: IntelCategoryRow[] };
  personas?: IntelPersona[];
  counts_by_ring: Record<'life' | 'social' | 'work', Record<string, number>>;
  pois: IntelPOI[];
  narrative: string;
  elapsed_s: number;
  fetched_at: string;
  _cache?: boolean;
}

// Build a GeoJSON polygon approximating a geodesic circle of radius_m
// around (lng, lat). 64 vertices is smooth enough for our zoom range
// and keeps the polygon cheap to render.
function circlePolygon(
  lng: number, lat: number, radiusM: number, n: number = 64,
): Array<Array<[number, number]>> {
  const coords: Array<[number, number]> = [];
  const earthR = 6371000;
  const latRad = lat * Math.PI / 180;
  const cosLat = Math.cos(latRad);
  for (let i = 0; i <= n; i++) {
    const bearing = (i / n) * 2 * Math.PI;
    // Local-tangent-plane approximation — fine for radii up to a few km.
    const dxDeg = (radiusM * Math.sin(bearing)) / (earthR * cosLat) * (180 / Math.PI);
    const dyDeg = (radiusM * Math.cos(bearing)) / earthR * (180 / Math.PI);
    coords.push([lng + dxDeg, lat + dyDeg]);
  }
  return [coords];
}
interface MarketEntry {
  name: string;
  state: string;
  cbsa: string;
  metrics: Record<string, MarketMetric | null>;
}
interface IndicatorDef {
  key: string;
  label: string;
  fmt: string;
  unit_hint: string;
  source_label: string;
}
interface MsaGeo {
  cbsa: string;
  name: string;
  state: string;
  population: number | null;
  latitude: number;
  longitude: number;
  score_total: number;
  rank: number;
}

interface PropGeo {
  name: string;
  location: string | null;
  lat: number;
  lng: number;
  price: number | null;
  price_unit: string | null;
  value: number | null;
  newness: number | null;
  amenity: number | null;
  income_min: number | null;
  bld_sample?: string[];
  unit_sample?: string[];
  leading?: Array<{ label: string; detail: string }>;
}

type SelectedKind =
  | { kind: 'msa'; cbsa: string }
  | { kind: 'property'; name: string };

// OpenFreeMap basemap styles — free, no API key, INCLUDE 3D building
// height data (render_height field on the 'building' source-layer).
// Carto's positron/dark-matter styles look similar but omit building
// heights, which is why 3D extrusion looked flat in v1.
const STYLE_LIGHT = 'https://tiles.openfreemap.org/styles/positron';
const STYLE_DARK  = 'https://tiles.openfreemap.org/styles/dark';

// ─── Layer system ─────────────────────────────────────────────────────
// Each layer picks one indicator from /me/markets and uses its value or
// YoY % to color the MSA cores. 'inverse: true' means lower = better.

interface MapLayer {
  key:     string;
  label:   string;
  sublabel: string;
  metric:  string;       // key into market.metrics
  useYoy?: boolean;      // color by YoY % delta instead of value
  inverse?: boolean;     // lower value = better (red for high)
  unit:    'pct' | 'money' | 'count' | 'persons' | 'index';
}

const MAP_LAYERS: MapLayer[] = [
  { key: 'rent_growth',  label: 'Rent growth',    sublabel: 'Multifamily YoY %',
    metric: 'zori_rent_index_mfr', useYoy: true, unit: 'pct' },
  { key: 'unemp',        label: 'Unemployment',   sublabel: 'BLS LAUS, low = better',
    metric: 'unemployment_rate_bls', inverse: true, unit: 'pct' },
  { key: 'permits',      label: 'Permit activity', sublabel: 'MF T12M',
    metric: 'permits_mf_t12m', unit: 'count' },
  { key: 'wage_growth',  label: 'Wage growth',    sublabel: 'QCEW YoY %',
    metric: 'avg_weekly_wage_qcew', useYoy: true, unit: 'pct' },
  { key: 'population',   label: 'Population',     sublabel: 'Census ACS',
    metric: 'population', unit: 'persons' },
  { key: 'rent',         label: 'Rent level',     sublabel: 'Current MF rent',
    metric: 'zori_rent_index_mfr', unit: 'money' },
];

// 3-stop heatmap ramp: red (bad) → gold (mid) → green (good)
function rampColor(t: number, inverse: boolean): string {
  const clamped = Math.max(0, Math.min(1, t));
  const x = inverse ? 1 - clamped : clamped;
  // Lerp red → gold → green
  if (x < 0.5) {
    const u = x * 2;
    return interpHex('#CC2200', '#C9A96E', u);
  }
  const u = (x - 0.5) * 2;
  return interpHex('#C9A96E', '#1A7A4A', u);
}

function interpHex(a: string, b: string, t: number): string {
  const ai = parseInt(a.slice(1), 16);
  const bi = parseInt(b.slice(1), 16);
  const ar = (ai >> 16) & 0xff, ag = (ai >> 8) & 0xff, ab = ai & 0xff;
  const br = (bi >> 16) & 0xff, bg = (bi >> 8) & 0xff, bb = bi & 0xff;
  const lerp = (x: number, y: number) => Math.round(x + (y - x) * t);
  return `#${[lerp(ar, br), lerp(ag, bg), lerp(ab, bb)]
    .map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

// Compute the raw value to color by (either the metric value or its YoY %)
// at the given year. If `year` is omitted, returns the latest available.
function valueForLayer(
  metrics: Record<string, MarketMetric | null>,
  layer: MapLayer,
  year?: number,
): number | null {
  const m = metrics[layer.metric];
  if (!m) return null;

  // If no specific year requested, use the metric's latest cached value
  // (and the precomputed YoY for that latest year).
  if (year === undefined || year === m.year) {
    return layer.useYoy ? (m.yoy_pct ?? null) : m.value;
  }

  // Look up the requested year in the series (sorted asc by year)
  const series = m.series || [];
  const entry = series.find(s => s.year === year);
  if (!entry) return null;

  if (!layer.useYoy) return entry.value;

  // Compute YoY from series: this year vs the immediately preceding year
  const prevIdx = series.findIndex(s => s.year === year) - 1;
  if (prevIdx < 0) return null;
  const prev = series[prevIdx]?.value;
  if (!prev || prev === 0) return null;
  return ((entry.value - prev) / prev) * 100;
}

// ── Trajectory ───────────────────────────────────────────────────────────
// Single bigger line chart for the active layer's metric on the selected
// MSA. Only renders the factual series points returned by the backend
// — no interpolation, no projections, no synthetic baselines. The
// horizontal axis labels the first, middle, and last real years; a
// vertical guide marks the year currently selected on the time slider
// and the dot sits exactly on the recorded value for that year.
function Trajectory(props: {
  series: Array<{ year: number; value: number }>;
  selectedYear: number;
  fmt: string;                   // 'pct' | 'money' | 'int_compact' | …
  fg: string;
  fgMute: string;
  accent: string;
  isDark: boolean;
}) {
  const { series, selectedYear, fmt, fg, fgMute, accent, isDark } = props;
  if (!series || series.length < 2) return null;

  const w = 540;
  const h = 110;
  const padL = 38;
  const padR = 10;
  const padT = 8;
  const padB = 22;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  // Sort by year so points connect chronologically. Backend should already
  // hand them back ASC, but defend against any stray ordering.
  const sorted = [...series].sort((a, b) => a.year - b.year);
  const xs = sorted.map(s => s.year);
  const ys = sorted.map(s => s.value);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const rangeX = Math.max(1, maxX - minX);
  // Pad the Y range slightly so the line doesn't kiss the edges.
  const yPad = Math.max((maxY - minY) * 0.08, Math.abs(maxY) * 0.02, 0.5);
  const loY = minY - yPad;
  const hiY = maxY + yPad;
  const rangeY = hiY - loY || 1;

  const toX = (year: number) => padL + ((year - minX) / rangeX) * innerW;
  const toY = (val: number)  => padT + innerH - ((val - loY) / rangeY) * innerH;

  const pts = sorted.map(s => ({ x: toX(s.year), y: toY(s.value), year: s.year, value: s.value }));
  const pathD = pts.map((p, i) =>
    `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`,
  ).join(' ');
  const areaD = `${pathD} L${pts[pts.length - 1].x.toFixed(1)},${(padT + innerH).toFixed(1)} L${pts[0].x.toFixed(1)},${(padT + innerH).toFixed(1)} Z`;

  // Dot position: only plot if the selected year is an ACTUAL data point.
  // (No interpolation between recorded years.)
  const dotEntry = sorted.find(s => s.year === selectedYear) || sorted[sorted.length - 1];
  const dotX = toX(dotEntry.year);
  const dotY = toY(dotEntry.value);

  // X-axis ticks: first, last, and one in the middle if there's room.
  const firstYear = sorted[0].year;
  const lastYear  = sorted[sorted.length - 1].year;
  const midYear   = sorted[Math.floor(sorted.length / 2)].year;
  const ticks = sorted.length >= 5 && midYear !== firstYear && midYear !== lastYear
    ? [firstYear, midYear, lastYear]
    : [firstYear, lastYear];

  // Y-axis: just min and max labels, very muted.
  const yMinLabel = fmtMetricCompact(minY, fmt);
  const yMaxLabel = fmtMetricCompact(maxY, fmt);

  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const lineColor = accent;
  const fillColor = `${accent}1f`;

  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}>
      {/* Horizontal guide lines at min + max */}
      <line x1={padL} y1={padT}        x2={w - padR} y2={padT}
        stroke={gridColor} strokeWidth={1} />
      <line x1={padL} y1={padT + innerH} x2={w - padR} y2={padT + innerH}
        stroke={gridColor} strokeWidth={1} />

      {/* Vertical guide at the currently-selected year */}
      <line x1={dotX} y1={padT - 2} x2={dotX} y2={padT + innerH + 4}
        stroke={accent} strokeWidth={1} strokeDasharray="2 3" opacity={0.45} />

      {/* Filled area + line */}
      <path d={areaD} fill={fillColor} />
      <path d={pathD} fill="none" stroke={lineColor} strokeWidth={2}
        strokeLinejoin="round" strokeLinecap="round" />

      {/* All recorded points (tiny dots) — emphasises that the chart
          plots real measurements, not a smoothed curve */}
      {pts.map(p => (
        <circle key={p.year} cx={p.x} cy={p.y} r={2.5}
          fill={isDark ? '#0a1220' : '#FFFFFF'}
          stroke={lineColor} strokeWidth={1.2} />
      ))}

      {/* Highlighted dot for selected year */}
      <circle cx={dotX} cy={dotY} r={5} fill={accent}
        stroke={isDark ? '#0a1220' : '#FFFFFF'} strokeWidth={2} />

      {/* Y-axis labels (min/max) */}
      <text x={padL - 6} y={padT + 4}
        textAnchor="end" fontSize={9} fill={fgMute}
        fontFamily="-apple-system, system-ui, sans-serif">
        {yMaxLabel}
      </text>
      <text x={padL - 6} y={padT + innerH + 2}
        textAnchor="end" fontSize={9} fill={fgMute}
        fontFamily="-apple-system, system-ui, sans-serif">
        {yMinLabel}
      </text>

      {/* X-axis year labels */}
      {ticks.map(y => (
        <text key={y} x={toX(y)} y={h - 6}
          textAnchor="middle" fontSize={10} fill={fgMute}
          fontFamily="-apple-system, system-ui, sans-serif">
          {y}
        </text>
      ))}

      {/* Value label tied to the highlighted dot */}
      <text x={dotX} y={dotY - 9}
        textAnchor="middle" fontSize={10} fontWeight={700} fill={fg}
        fontFamily="Fraunces, Georgia, serif">
        {fmtMetricCompact(dotEntry.value, fmt)}
      </text>
    </svg>
  );
}

// Compact metric formatter used by the trajectory chart axes.
function fmtMetricCompact(value: number, fmt: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  if (fmt === 'pct')     return `${value.toFixed(1)}%`;
  if (fmt === 'decimal') return value.toFixed(1);
  if (fmt === 'money' || fmt === 'money_per_month') {
    if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(0)}k`;
    return `$${Math.round(value).toLocaleString()}`;
  }
  if (fmt === 'int_compact') {
    if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
    return Math.round(value).toLocaleString();
  }
  return Math.round(value).toLocaleString();
}

// Get the union of years that have any data across all loaded MSAs.
// Used to populate the time-slider's year range.
function availableYears(
  markets: MarketEntry[],
  layer: MapLayer,
): number[] {
  const yrs = new Set<number>();
  for (const mk of markets) {
    const m = mk.metrics[layer.metric];
    if (!m) continue;
    for (const s of m.series || []) yrs.add(s.year);
  }
  return Array.from(yrs).sort();
}

export default function CityMap({ token, onExitToBoard, onExitToDashboard, onOpenExplorer }: Props) {
  const [theme, setTheme] = useState<Theme>(() => {
    return (localStorage.getItem('coast.map.theme') as Theme) || 'dark';
  });
  const [selected, setSelected] = useState<SelectedKind | null>(null);
  const [msaList, setMsaList] = useState<Array<{
    cbsa: string; name: string; state: string;
    lat: number; lng: number; population: number | null;
  }>>([]);
  const [marketData, setMarketData] = useState<{
    markets: MarketEntry[]; indicators: IndicatorDef[];
  } | null>(null);
  const [properties, setProperties] = useState<PropGeo[]>([]);
  // Loading flags so the empty state can clear correctly even when one
  // source is empty by design (e.g. no tracked properties).
  const [msaLoading,   setMsaLoading]   = useState(true);
  const [propsLoading, setPropsLoading] = useState(true);
  // True after we've auto-fit the camera to user data the first time —
  // we only want to auto-fit once on initial load, not after every state
  // change.
  const [didInitialFit, setDidInitialFit] = useState(false);
  const [activeLayer, setActiveLayer] = useState<string>('rent_growth');
  const [layerOpen, setLayerOpen]     = useState(false);
  const [heatmapOn, setHeatmapOn]     = useState(true);
  // null = use latest available year per metric. Set by the slider.
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  // Autoplay state — when true, an interval advances the year forward,
  // looping back to the earliest year on overflow. Any manual scrub
  // pauses playback so the user stays in control.
  const [playing, setPlaying] = useState(false);
  // Time-slider expansion. Default is a small bottom-center pill that
  // shows just the year + active layer; expanding reveals the scrubber
  // track and tick marks. Playback forces expansion.
  const [sliderExpanded, setSliderExpanded] = useState(false);
  // Top-movers panel — collapsed pill by default, mirroring the slider on
  // the opposite corner so neither blocks the center of the map.
  const [moversOpen, setMoversOpen] = useState(false);
  // Property-Intel mode — the behavioural intelligence layer. When active
  // the map flies to a specific property and overlays human-experience
  // rings + a lifestyle-scored POI canvas with an Apple-Maps-ish UI.
  const [intelOpen, setIntelOpen] = useState(false);
  const [intelData, setIntelData] = useState<IntelBundle | null>(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [intelError, setIntelError] = useState<string | null>(null);
  const [intelCategoryFilter, setIntelCategoryFilter] = useState<string | null>(null);
  // Address the user typed into the panel's search box. When non-empty,
  // the intel fetch uses /intel/location?address=... and bypasses the
  // Parker default — letting the same panel work for any searched place.
  const [intelSearchInput, setIntelSearchInput] = useState<string>('');
  const [intelSearchAddress, setIntelSearchAddress] = useState<string | null>(null);

  // ── Deeplink: /map?intel_address=...
  //   Lets the Board's entity chips ("Test on Map" buttons under each
  //   voice memo's address) jump straight into the intel panel for that
  //   address — no manual search needed.
  //   Listening on popstate covers both the cold-mount case (came in
  //   from Board) and the in-page case (the user dispatched popstate
  //   while CityMap was already mounted).
  //   We strip the query param after consumption so a refresh isn't
  //   re-interpreted as a fresh deeplink on top of any new user action.
  useEffect(() => {
    const consumeDeeplink = () => {
      const params = new URLSearchParams(window.location.search);
      const deeplink = params.get('intel_address');
      if (!deeplink || !deeplink.trim()) return;
      const addr = deeplink.trim();
      setIntelOpen(true);
      setIntelData(null);
      setIntelError(null);
      setIntelSearchInput(addr);
      setIntelSearchAddress(addr);
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    };
    consumeDeeplink();
    window.addEventListener('popstate', consumeDeeplink);
    return () => window.removeEventListener('popstate', consumeDeeplink);
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    label:   string;       // shown to user
    sub?:    string;       // smaller subtitle
    kind:    'property' | 'msa' | 'address';
    lat:     number;
    lng:     number;
    zoom?:   number;
  }>>([]);
  const [searching, setSearching] = useState(false);

  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<maplibregl.Map | null>(null);
  // Keyed marker registry so we can re-style colors smoothly when the
  // active layer / selected year changes, without rebuilding the DOM
  // node (which would lose any in-flight CSS transition).
  const markersRef = useRef<Map<string, {
    marker: maplibregl.Marker;
    inner: HTMLDivElement;
    dot?: HTMLDivElement;
    kind: 'msa' | 'property';
    cbsa?: string;
  }>>(new Map());

  // ── Autoplay tick ────────────────────────────────────────────────────
  // Advances selectedYear forward at a fixed cadence while `playing` is
  // true. On overflow (past the latest year for the active layer) it
  // loops back to the earliest year so the loop feels seamless. Stops
  // automatically if the layer has fewer than 2 years of data.
  useEffect(() => {
    if (!playing) return;
    if (!marketData) return;
    const layer = MAP_LAYERS.find(l => l.key === activeLayer) || MAP_LAYERS[0];
    const yrs = availableYears(marketData.markets, layer);
    if (yrs.length < 2) { setPlaying(false); return; }
    const minY = yrs[0];
    const maxY = yrs[yrs.length - 1];
    const i = window.setInterval(() => {
      setSelectedYear(prev => {
        const cur = prev ?? maxY;
        const idx = yrs.indexOf(cur);
        const nextIdx = idx < 0 ? 0 : idx + 1;
        if (nextIdx >= yrs.length) {
          // Loop back to earliest year for a continuous "playback".
          return minY;
        }
        const nxt = yrs[nextIdx];
        // If we just landed back on latest, expose as live (null).
        return nxt === maxY ? null : nxt;
      });
    }, 1400);
    return () => window.clearInterval(i);
  }, [playing, marketData, activeLayer]);

  // ── Initial map setup ────────────────────────────────────────────────

  useEffect(() => {
    if (map.current || !mapContainer.current) return;
    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: theme === 'dark' ? STYLE_DARK : STYLE_LIGHT,
      center: [-98.5, 39.5],   // US centroid
      zoom: 3.4,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
    });
    map.current.addControl(new maplibregl.AttributionControl({
      compact: true,
    }), 'bottom-left');
    map.current.addControl(new maplibregl.NavigationControl({
      showCompass: false,
    }), 'top-right');
    map.current.on('click', () => setSelected(null));

    // 3D BUILDINGS at high zoom. Carto Positron / Dark Matter both expose
    // a 'carto' source with a 'building' source-layer. When the style
    // finishes loading, add a fill-extrusion that fades in past zoom 14.
    const addBuildings = () => {
      const m = map.current;
      if (!m) return;
      const style = m.getStyle();
      // Find a source that has 'building' data — Carto styles use 'carto'
      const candidateSources = Object.keys(style.sources || {});
      let added = false;
      for (const sourceId of candidateSources) {
        const s = style.sources[sourceId];
        // Vector sources only; skip raster/geojson
        if ((s as { type?: string }).type !== 'vector') continue;
        try {
          if (!m.getLayer('coast-3d-buildings')) {
            m.addLayer({
              id: 'coast-3d-buildings',
              source: sourceId,
              'source-layer': 'building',
              type: 'fill-extrusion',
              minzoom: 14,
              paint: {
                'fill-extrusion-color': theme === 'dark'
                  ? ['interpolate', ['linear'], ['zoom'], 14, '#2A2A30', 17, '#3A3A45']
                  : ['interpolate', ['linear'], ['zoom'], 14, '#E5E5E5', 17, '#D0D0D0'],
                'fill-extrusion-height': [
                  'case',
                  ['has', 'render_height'], ['get', 'render_height'],
                  ['has', 'height'],        ['get', 'height'],
                  10, // sensible default for buildings with no height tag
                ],
                'fill-extrusion-base': 0,
                'fill-extrusion-opacity': [
                  'interpolate', ['linear'], ['zoom'], 14, 0, 15.5, 0.85,
                ],
                'fill-extrusion-vertical-gradient': true,
              },
            });
            added = true;
            break;
          }
        } catch { /* try next source */ }
      }
      if (!added) {
        // Style may not have a 'building' source-layer; silently skip.
      }
    };
    map.current.on('style.load', addBuildings);
    // Also try right after the map's initial style loads
    if (map.current.isStyleLoaded()) addBuildings();
    else map.current.once('load', addBuildings);

    // Cleanup on unmount: tear down MapLibre instance to free GPU + memory
    return () => {
      markersRef.current.forEach(v => v.marker.remove());
      markersRef.current.clear();
      if (map.current) {
        try { map.current.remove(); } catch { /* ignore */ }
        map.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Theme-switch: hot-swap the style without destroying the map
  useEffect(() => {
    localStorage.setItem('coast.map.theme', theme);
    if (!map.current) return;
    map.current.setStyle(theme === 'dark' ? STYLE_DARK : STYLE_LIGHT);
  }, [theme]);

  // Auto-fit camera to include every MSA + property the first time data
  // loads. Subsequent state changes don't refit — user is free to pan.
  useEffect(() => {
    if (didInitialFit || !map.current) return;
    if (msaList.length === 0 && properties.length === 0) return;
    const allPoints: Array<[number, number]> = [
      ...msaList.map(m => [m.lng, m.lat] as [number, number]),
      ...properties.map(p => [p.lng, p.lat] as [number, number]),
    ];
    if (allPoints.length === 0) return;
    // Compute bounding box
    let minLng = allPoints[0][0], maxLng = allPoints[0][0];
    let minLat = allPoints[0][1], maxLat = allPoints[0][1];
    for (const [lng, lat] of allPoints) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    // Pad a bit on each side
    const padding = { top: 120, right: 80, bottom: 80, left: 80 };
    map.current.fitBounds(
      [[minLng, minLat], [maxLng, maxLat]],
      { padding, duration: 1200, maxZoom: 8 },
    );
    setDidInitialFit(true);
  }, [msaList, properties, didInitialFit]);

  // ── Fetch user's MSAs + property pins ────────────────────────────────

  useEffect(() => {
    let alive = true;
    // Markets (also contains the lat/lng for each focused MSA)
    axios.get(`${API}/me/markets`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      if (!alive) return;
      setMarketData({
        markets: r.data?.markets || [],
        indicators: r.data?.indicators || [],
      });
    }).catch(() => {});

    // MSA coordinates come from /searcher/rankings (we filter by focus CBSAs)
    axios.get(`${API}/me/focus`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      const focus = r.data?.focus;
      if (!focus?.msas) {
        if (alive) setMsaLoading(false);
        return;
      }
      const cbsas = (focus.msas as Array<{ cbsa?: string }>)
        .map(m => m.cbsa).filter(Boolean) as string[];
      const rank = await axios.get(`${API}/searcher/rankings?limit=1500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const matched: Array<{
        cbsa: string; name: string; state: string;
        lat: number; lng: number; population: number | null;
      }> = [];
      for (const m of (rank.data?.metros || []) as Array<{
        cbsa_code: string; name: string; population: number | null;
        latitude: number; longitude: number;
      }>) {
        if (cbsas.includes(m.cbsa_code)) {
          const stateMatch = m.name.match(/,\s*([A-Z]{2})/);
          matched.push({
            cbsa: m.cbsa_code,
            name: m.name.replace(/,\s*[A-Z]{2}.*$/, ''),
            state: stateMatch?.[1] || '',
            lat: m.latitude, lng: m.longitude,
            population: m.population,
          });
        }
      }
      if (alive) {
        setMsaList(matched);
        setMsaLoading(false);
      }
    }).catch(() => { if (alive) setMsaLoading(false); });

    // Tracked properties — dynamically pick the user's first connected sheet
    // (fail gracefully if they have none yet).
    axios.get(`${API}/sheets/list`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      const sheets = r.data?.sheets || [];
      if (sheets.length === 0) {
        if (alive) { setProperties([]); setPropsLoading(false); }
        return;
      }
      const sid = sheets[0].id;
      const g = await axios.get(`${API}/sheets/${sid}/geo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (alive) {
        setProperties(g.data?.properties || []);
        setPropsLoading(false);
      }
    }).catch(() => { if (alive) setPropsLoading(false); });

    return () => { alive = false; };
  }, [token]);

  // ── Plot markers ─────────────────────────────────────────────────────
  // Two effects:
  //   1) BUILD — runs only when the set of markers or the theme changes.
  //      Creates DOM nodes once and stores them in a keyed Map so we can
  //      re-style them later without re-creating them.
  //   2) RESTYLE — runs when the active layer / market data / selected
  //      year changes. Just mutates existing inner+dot styles, letting
  //      CSS transitions smoothly tween the colour between years.

  useEffect(() => {
    if (!map.current) return;

    // Tear down any existing markers before rebuilding
    markersRef.current.forEach(v => v.marker.remove());
    markersRef.current.clear();

    const accent = '#C9A96E';

    // CRITICAL: MapLibre sets `transform: translate(...)` on the marker's
    // root element every frame to position it on the map. If we set our
    // own `transform: scale(...)` on that root element (e.g. for hover),
    // we OVERWRITE the positioning transform — the marker snaps to (0,0).
    // Solution: keep the root element untouched, wrap everything visual in
    // an INNER div, and apply hover scale to the inner div.
    for (const m of msaList) {
      const popScale = m.population
        ? Math.max(0.6, Math.min(1.6, Math.log10(m.population) - 4.5))
        : 0.8;
      const size = Math.round(34 * popScale);

      const root = document.createElement('div');
      root.style.width  = `${size}px`;
      root.style.height = `${size}px`;
      root.style.cursor = 'pointer';

      const inner = document.createElement('div');
      inner.style.cssText = `
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background-color: ${accent}33;
        position: relative;
        transition: transform 0.18s cubic-bezier(0.16, 1, 0.3, 1),
                    background-color 0.7s ease,
                    box-shadow 0.7s ease;
        box-shadow: 0 0 14px 4px ${accent}55, 0 0 4px ${accent}88;
        will-change: transform;
      `;
      root.appendChild(inner);

      const dot = document.createElement('div');
      dot.style.cssText = `
        position: absolute;
        inset: 30%;
        background-color: ${accent};
        border-radius: 50%;
        border: 2px solid ${theme === 'dark' ? '#0F0F12' : '#FFFFFF'};
        box-shadow: 0 0 12px ${accent}, 0 0 2px rgba(0,0,0,0.4);
        transition: background-color 0.7s ease,
                    box-shadow 0.7s ease;
      `;
      inner.appendChild(dot);

      const label = document.createElement('div');
      label.textContent = `${m.name}, ${m.state}`;
      label.style.cssText = `
        position: absolute;
        top: calc(100% + 4px);
        left: 50%;
        transform: translateX(-50%);
        color: ${theme === 'dark' ? '#F0F0F0' : '#1A1A1A'};
        background: ${theme === 'dark' ? 'rgba(15,15,18,0.85)' : 'rgba(255,255,255,0.92)'};
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        font-family: -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
        padding: 3px 8px;
        border-radius: 8px;
        border: 1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
        white-space: nowrap;
        pointer-events: none;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      `;
      inner.appendChild(label);

      inner.addEventListener('mouseenter', () => { inner.style.transform = 'scale(1.15)'; });
      inner.addEventListener('mouseleave', () => { inner.style.transform = 'scale(1)';   });
      inner.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        setSelected({ kind: 'msa', cbsa: m.cbsa });
        map.current?.flyTo({
          center: [m.lng, m.lat],
          zoom: 9.5,
          speed: 1.0,
          curve: 1.4,
          essential: true,
        });
      });

      const marker = new maplibregl.Marker({
        element: root,
        anchor: 'center',
      })
        .setLngLat([m.lng, m.lat])
        .addTo(map.current);
      markersRef.current.set(`msa:${m.cbsa}`, {
        marker, inner, dot, kind: 'msa', cbsa: m.cbsa,
      });
    }

    // Property pins — same architecture (root untouched, inner handles hover)
    for (const p of properties) {
      const root = document.createElement('div');
      root.style.width  = '20px';
      root.style.height = '20px';
      root.style.cursor = 'pointer';

      const inner = document.createElement('div');
      inner.style.cssText = `
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background-color: ${accent};
        border: 2px solid ${theme === 'dark' ? '#0F0F12' : '#FFFFFF'};
        box-shadow: 0 0 8px ${accent}88, 0 2px 4px rgba(0,0,0,0.3);
        transition: transform 0.15s, box-shadow 0.15s;
        will-change: transform;
      `;
      root.appendChild(inner);

      inner.addEventListener('mouseenter', () => {
        inner.style.transform = 'scale(1.35)';
        inner.style.boxShadow = `0 0 16px ${accent}, 0 4px 8px rgba(0,0,0,0.35)`;
      });
      inner.addEventListener('mouseleave', () => {
        inner.style.transform = 'scale(1)';
        inner.style.boxShadow = `0 0 8px ${accent}88, 0 2px 4px rgba(0,0,0,0.3)`;
      });
      inner.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        setSelected({ kind: 'property', name: p.name });
        map.current?.flyTo({
          center: [p.lng, p.lat],
          zoom: 15,
          speed: 1.2,
          curve: 1.4,
          essential: true,
        });
      });

      const marker = new maplibregl.Marker({
        element: root,
        anchor: 'center',
      })
        .setLngLat([p.lng, p.lat])
        .addTo(map.current);
      markersRef.current.set(`prop:${p.name}:${p.lat}:${p.lng}`, {
        marker, inner, kind: 'property',
      });
    }
  }, [msaList, properties, theme]);

  // RESTYLE — smooth color transitions on existing markers when the
  // active layer or selected year changes. No DOM rebuild → CSS
  // transitions fire and the dots tween between colors.
  useEffect(() => {
    if (!map.current) return;
    const accent = '#C9A96E';

    const layer = MAP_LAYERS.find(l => l.key === activeLayer) || MAP_LAYERS[0];
    const allValues: number[] = [];
    if (marketData) {
      for (const mk of marketData.markets) {
        const v = valueForLayer(mk.metrics, layer, selectedYear ?? undefined);
        if (v !== null && v !== undefined && Number.isFinite(v))
          allValues.push(v);
      }
    }
    const layerMin = allValues.length ? Math.min(...allValues) : 0;
    const layerMax = allValues.length ? Math.max(...allValues) : 1;
    const layerSpan = layerMax - layerMin || 1;
    const isDarkLocal = theme === 'dark';

    markersRef.current.forEach((rec) => {
      if (rec.kind !== 'msa' || !rec.cbsa) return;
      const market = marketData?.markets.find(x => x.cbsa === rec.cbsa);
      const v = market ? valueForLayer(market.metrics, layer, selectedYear ?? undefined) : null;
      const t = (v !== null && v !== undefined && Number.isFinite(v))
              ? (v - layerMin) / layerSpan : 0.5;
      const c = v === null ? accent : rampColor(t, !!layer.inverse);
      // Inner halo: soft, low-alpha background + matching glow shadow.
      rec.inner.style.backgroundColor = c + '33';
      rec.inner.style.boxShadow = `0 0 18px 5px ${c}55, 0 0 6px ${c}aa`;
      // Bright core dot — solid color + matching tight glow.
      if (rec.dot) {
        rec.dot.style.backgroundColor = c;
        rec.dot.style.boxShadow = `0 0 12px ${c}, 0 0 2px ${isDarkLocal ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.18)'}`;
      }
    });
  }, [activeLayer, marketData, selectedYear, theme]);

  // ── Heatmap overlay: large soft-edged colored circles per MSA ────────
  // Activated by 'heatmapOn'. Updates source data when layer or markets
  // change. Uses a circle layer with high blur for the "heat" look.

  useEffect(() => {
    const m = map.current;
    if (!m) return;

    const SOURCE_ID = 'coast-msa-heat';
    const LAYER_ID  = 'coast-msa-heat-circle';

    const ensureLayer = () => {
      if (!m.isStyleLoaded()) return;
      const layer = MAP_LAYERS.find(l => l.key === activeLayer) || MAP_LAYERS[0];

      // Build a FeatureCollection with one feature per MSA. The numeric
      // id is the CBSA — promoted via `promoteId` — so we can drive each
      // circle's COLOR through feature-state, which MapLibre then
      // transitions smoothly. Color is NOT baked into properties.
      const features = msaList.map(ms => ({
        type: 'Feature' as const,
        id: parseInt(ms.cbsa, 10),
        geometry: { type: 'Point' as const, coordinates: [ms.lng, ms.lat] },
        properties: {
          cbsa: ms.cbsa,
          name: ms.name,
        },
      }));
      const data = { type: 'FeatureCollection' as const, features };

      const existing = m.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      if (existing) {
        existing.setData(data);
      } else {
        m.addSource(SOURCE_ID, {
          type: 'geojson',
          data,
          promoteId: 'cbsa',
        });
      }

      if (!m.getLayer(LAYER_ID)) {
        m.addLayer({
          id: LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          paint: {
            'circle-radius': [
              'interpolate', ['exponential', 1.6], ['zoom'],
              3,   55,
              5,   90,
              7,  140,
              9,  220,
              12, 360,
            ],
            // Color is driven by feature-state.tNorm (0..1). When that
            // value changes, MapLibre interpolates between the old and
            // new colour over the `-transition` duration below.
            'circle-color': [
              'interpolate', ['linear'],
              ['coalesce', ['feature-state', 'tNorm'], 0.5],
              0.0, '#CC2200',
              0.5, '#C9A96E',
              1.0, '#1A7A4A',
            ],
            'circle-color-transition': { duration: 900, delay: 0 },
            'circle-opacity': [
              'interpolate', ['linear'], ['zoom'],
              3, 0.35,
              7, 0.30,
              10, 0.18,
              13, 0.0,
            ],
            'circle-blur': 1.0,
            'circle-stroke-width': 0,
          } as any,
          layout: { visibility: heatmapOn ? 'visible' : 'none' },
        }, firstSymbolId(m));
      } else {
        m.setLayoutProperty(LAYER_ID, 'visibility',
          heatmapOn ? 'visible' : 'none');
      }

      // Push the normalized per-MSA value into feature-state so the
      // color expression picks it up. Inverse layers flip the ramp by
      // storing (1 - t).
      const allVals: number[] = [];
      if (marketData) {
        for (const mk of marketData.markets) {
          const v = valueForLayer(mk.metrics, layer, selectedYear ?? undefined);
          if (v !== null && v !== undefined && Number.isFinite(v))
            allVals.push(v);
        }
      }
      const lo = allVals.length ? Math.min(...allVals) : 0;
      const hi = allVals.length ? Math.max(...allVals) : 1;
      const span = hi - lo || 1;
      for (const ms of msaList) {
        const market = marketData?.markets.find(x => x.cbsa === ms.cbsa);
        const v = market ? valueForLayer(market.metrics, layer, selectedYear ?? undefined) : null;
        let t = (v !== null && v !== undefined && Number.isFinite(v))
              ? (v - lo) / span : 0.5;
        if (layer.inverse) t = 1 - t;
        // Clamp to keep the expression well-defined
        t = Math.max(0, Math.min(1, t));
        m.setFeatureState({ source: SOURCE_ID, id: parseInt(ms.cbsa, 10) }, { tNorm: t });
      }
    };

    if (m.isStyleLoaded()) ensureLayer();
    const handler = () => ensureLayer();
    m.on('style.load', handler);
    m.on('load', handler);
    return () => {
      m.off('style.load', handler);
      m.off('load', handler);
    };
  }, [msaList, marketData, activeLayer, heatmapOn, theme, selectedYear]);

  // ── Property Intelligence overlay ────────────────────────────────────
  // Fetches the intel bundle for The Parker when the user opens Intel
  // mode, then renders three concentric "human experience" rings + a
  // POI canvas on the map. The side panel is in JSX further down.

  // Track whether the user explicitly asked for a refresh (vs. a normal
  // open), so we can force the backend to bust its cache.
  const intelRefreshRef = useRef(false);
  useEffect(() => {
    if (!intelOpen || intelData) return;
    let cancelled = false;
    setIntelLoading(true);
    setIntelError(null);
    const refresh = intelRefreshRef.current;
    intelRefreshRef.current = false;

    // Dispatch on the search address: if the user has typed and submitted
    // an address in the panel, use the new generic /intel/location
    // endpoint. Otherwise fall back to the Parker demo so opening the
    // panel cold still shows something familiar.
    const url = intelSearchAddress
      ? `${API}/intel/location`
      : `${API}/intel/property/parker`;
    const params: Record<string, string> = {};
    if (intelSearchAddress) params.address = intelSearchAddress;
    if (refresh) params.refresh = 'true';

    axios.get(url, {
      params,
      headers: { Authorization: `Bearer ${token}` },
      // Overpass + Nominatim + Claude together can run 20–40s in dense
      // urban areas. 90s leaves comfortable headroom before the browser
      // would consider the request stuck.
      timeout: 90000,
    }).then(r => {
      if (cancelled) return;
      setIntelData(r.data as IntelBundle);
    }).catch(err => {
      if (cancelled) return;
      // Surface as much detail as we can so the user knows whether it's
      // a bad address, a still-cold backend, or a network blip.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail
                  || err?.response?.data?.message
                  || err?.message;
      let msg: string;
      if (status === 404) {
        msg = intelSearchAddress
          ? `Could not find that location: ${intelSearchAddress}`
          : (detail || `No tracked property matching "parker" found in your sheets.`);
      } else if (status === 401) {
        msg = 'Session expired — sign in again to load Intel.';
      } else if (status === 503) {
        msg = `OpenStreetMap is currently unavailable. ${detail || ''}`.trim();
      } else if (status) {
        msg = `Intel error (${status}): ${detail || 'unknown'}`;
      } else if (err?.code === 'ECONNABORTED') {
        msg = 'Intel request timed out — the backend is slow or down. Try again.';
      } else {
        msg = `Could not reach the backend. ${detail || ''}`.trim();
      }
      setIntelError(msg);
    }).finally(() => {
      if (!cancelled) setIntelLoading(false);
    });
    return () => { cancelled = true; };
  }, [intelOpen, intelData, token, intelSearchAddress]);

  // Fly to the property + drop the spatial canvas when intel opens.
  useEffect(() => {
    const m = map.current;
    if (!m || !intelData || !intelOpen) return;
    m.flyTo({
      center: [intelData.property.lng, intelData.property.lat],
      zoom: 14.2,
      speed: 0.9,
      curve: 1.4,
      essential: true,
    });

    const RING_SOURCE = 'coast-intel-rings';
    const POI_SOURCE  = 'coast-intel-pois';
    const CENTER_SRC  = 'coast-intel-center';
    const RING_FILL   = 'coast-intel-rings-fill';
    const RING_LINE   = 'coast-intel-rings-line';
    const POI_LAYER   = 'coast-intel-pois-circle';
    const POI_GLOW    = 'coast-intel-pois-glow';
    const CENTER_LYR  = 'coast-intel-center-circle';

    const apply = () => {
      if (!m.isStyleLoaded()) return;
      // Rings as GeoJSON polygons (outer → inner so inner fills on top)
      const ringFeats = [...intelData.rings]
        .sort((a, b) => b.radius_m - a.radius_m)
        .map((r, i) => ({
          type: 'Feature' as const,
          properties: {
            key:        r.key,
            label:      r.label,
            radius:     r.radius_m,
            // Opacity tier: outer = faint, inner = stronger
            fillOp:     i === 0 ? 0.06 : i === 1 ? 0.09 : 0.14,
            lineOp:     i === 0 ? 0.35 : i === 1 ? 0.55 : 0.85,
          },
          geometry: {
            type: 'Polygon' as const,
            coordinates: circlePolygon(
              intelData.property.lng,
              intelData.property.lat,
              r.radius_m,
            ),
          },
        }));
      const ringFC = { type: 'FeatureCollection' as const, features: ringFeats };

      const ringSrc = m.getSource(RING_SOURCE) as maplibregl.GeoJSONSource | undefined;
      if (ringSrc) ringSrc.setData(ringFC);
      else m.addSource(RING_SOURCE, { type: 'geojson', data: ringFC });

      if (!m.getLayer(RING_FILL)) {
        m.addLayer({
          id: RING_FILL, source: RING_SOURCE, type: 'fill',
          paint: {
            'fill-color': '#C9A96E',
            'fill-opacity': ['get', 'fillOp'],
          },
        }, firstSymbolId(m));
      }
      if (!m.getLayer(RING_LINE)) {
        m.addLayer({
          id: RING_LINE, source: RING_SOURCE, type: 'line',
          paint: {
            'line-color': '#C9A96E',
            'line-opacity': ['get', 'lineOp'],
            'line-width': 1.6,
            'line-dasharray': [2, 3],
          },
        }, firstSymbolId(m));
      }

      // POIs — single circle layer styled by feature color
      const palette: Record<string, string> = {};
      for (const c of intelData.categories) palette[c.key] = c.color;
      const visibleCat = intelCategoryFilter;
      const poiFeats = intelData.pois
        .filter(p => !visibleCat || p.category === visibleCat)
        .map(p => ({
          type: 'Feature' as const,
          properties: {
            name: p.name, cat: p.category, ring: p.ring,
            color: palette[p.category] || '#C9A96E',
          },
          geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
        }));
      const poiFC = { type: 'FeatureCollection' as const, features: poiFeats };
      const poiSrc = m.getSource(POI_SOURCE) as maplibregl.GeoJSONSource | undefined;
      if (poiSrc) poiSrc.setData(poiFC);
      else m.addSource(POI_SOURCE, { type: 'geojson', data: poiFC });

      if (!m.getLayer(POI_GLOW)) {
        m.addLayer({
          id: POI_GLOW, source: POI_SOURCE, type: 'circle',
          paint: {
            'circle-radius': 9,
            'circle-color': ['get', 'color'],
            'circle-opacity': 0.22,
            'circle-blur': 0.6,
          },
        });
      }
      if (!m.getLayer(POI_LAYER)) {
        m.addLayer({
          id: POI_LAYER, source: POI_SOURCE, type: 'circle',
          paint: {
            'circle-radius': [
              'interpolate', ['linear'], ['zoom'],
              12, 2,
              14, 3.5,
              16, 5,
            ],
            'circle-color': ['get', 'color'],
            'circle-stroke-color': theme === 'dark' ? '#0a1220' : '#FFFFFF',
            'circle-stroke-width': 1.2,
            'circle-opacity': 0.95,
          },
        });
      }

      // Center anchor — pulsing gold dot for the property
      const centerFC = {
        type: 'FeatureCollection' as const,
        features: [{
          type: 'Feature' as const,
          properties: { name: intelData.property.name },
          geometry: {
            type: 'Point' as const,
            coordinates: [intelData.property.lng, intelData.property.lat],
          },
        }],
      };
      const centerSrc = m.getSource(CENTER_SRC) as maplibregl.GeoJSONSource | undefined;
      if (centerSrc) centerSrc.setData(centerFC);
      else m.addSource(CENTER_SRC, { type: 'geojson', data: centerFC });
      if (!m.getLayer(CENTER_LYR)) {
        m.addLayer({
          id: CENTER_LYR, source: CENTER_SRC, type: 'circle',
          paint: {
            'circle-radius': 9,
            'circle-color': '#C9A96E',
            'circle-stroke-color': theme === 'dark' ? '#0a1220' : '#FFFFFF',
            'circle-stroke-width': 2.5,
          },
        });
      }
    };

    if (m.isStyleLoaded()) apply();
    const onLoad = () => apply();
    m.on('style.load', onLoad);
    m.on('load', onLoad);

    return () => {
      m.off('style.load', onLoad);
      m.off('load', onLoad);
      // Tear-down — only when intel closes entirely (handled by another effect).
    };
  }, [intelOpen, intelData, intelCategoryFilter, theme]);

  // Cleanup layers/sources when intel mode is closed
  useEffect(() => {
    if (intelOpen) return;
    const m = map.current;
    if (!m) return;
    const ids = [
      'coast-intel-rings-fill', 'coast-intel-rings-line',
      'coast-intel-pois-glow', 'coast-intel-pois-circle',
      'coast-intel-center-circle',
    ];
    for (const id of ids) {
      if (m.getLayer(id)) m.removeLayer(id);
    }
    for (const s of ['coast-intel-rings', 'coast-intel-pois', 'coast-intel-center']) {
      if (m.getSource(s)) m.removeSource(s);
    }
  }, [intelOpen]);

  // ── Selected entity for panel ────────────────────────────────────────

  const selectedMsa = selected?.kind === 'msa'
    ? marketData?.markets.find(m => m.cbsa === selected.cbsa)
    : null;
  const selectedMsaGeo = selected?.kind === 'msa'
    ? msaList.find(m => m.cbsa === selected.cbsa)
    : null;
  const selectedProp = selected?.kind === 'property'
    ? properties.find(p => p.name === selected.name)
    : null;

  // ── Theme tokens ─────────────────────────────────────────────────────

  const accent = '#C9A96E';
  const isDark = theme === 'dark';
  const pillBg     = isDark ? 'rgba(15,15,18,0.78)' : 'rgba(255,255,255,0.82)';
  const pillBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const fg         = isDark ? '#F0F0F0' : '#1A1A1A';
  const fgMute     = isDark ? '#9CA3AF' : '#6B7280';

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div style={{
      position: 'fixed', inset: 0, overflow: 'hidden',
      background: isDark ? '#0F0F12' : '#FAFAF9',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter", system-ui, sans-serif',
    }}>
      <style>{`
        @keyframes coastSlideUp {
          from { transform: translateY(120%); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        @keyframes coastFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes coastFadeInRight {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes coastPulseSlow {
          0%, 100% { box-shadow: 0 0 0 4px ${accent}33, 0 0 16px ${accent}55; }
          50%      { box-shadow: 0 0 0 8px ${accent}22, 0 0 22px ${accent}88; }
        }
        .maplibregl-ctrl-attrib { font-size: 9px !important; }

        /* Time-slider track — Apple-Maps-y, gold thumb, subtle rail */
        input.coast-time-slider {
          -webkit-appearance: none; appearance: none;
          width: 100%; height: 6px;
          background: ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)'};
          border-radius: 3px;
          outline: none;
          padding: 0;
          margin: 0;
        }
        input.coast-time-slider::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 18px; height: 18px; border-radius: 9px;
          background: ${accent};
          border: 2.5px solid ${isDark ? '#0a1220' : '#FFFFFF'};
          cursor: grab;
          box-shadow: 0 2px 6px rgba(0,0,0,0.35), 0 0 0 1px ${accent}66;
          transition: transform 0.15s ease;
        }
        input.coast-time-slider::-webkit-slider-thumb:active { cursor: grabbing; transform: scale(1.15); }
        input.coast-time-slider::-moz-range-thumb {
          width: 18px; height: 18px; border-radius: 9px;
          background: ${accent};
          border: 2.5px solid ${isDark ? '#0a1220' : '#FFFFFF'};
          cursor: grab;
          box-shadow: 0 2px 6px rgba(0,0,0,0.35);
        }
      `}</style>

      {/* Map */}
      <div ref={mapContainer} style={{ position: 'absolute', inset: 0 }} />

      {/* Top-left brand pill */}
      <div style={{
        position: 'absolute', top: 18, left: 18,
        display: 'flex', gap: 8, alignItems: 'center',
      }}>
        <div style={glassPill(pillBg, pillBorder, isDark)}>
          <span style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 18, fontWeight: 800, color: accent,
            letterSpacing: '0.02em',
          }}>
            COAST
          </span>
          <span style={{
            color: fgMute, fontSize: 10, marginLeft: 8,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            fontWeight: 600,
          }}>
            Map · v1
          </span>
        </div>
        <button
          onClick={() => setIntelOpen(o => !o)}
          title="Property Intelligence — behavioural map of The Parker, Los Angeles"
          style={{
            ...glassBtn(pillBg, pillBorder, intelOpen ? '#FFFFFF' : accent, isDark),
            background: intelOpen ? accent : `${accent}22`,
            border: `1px solid ${accent}`,
            fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
            fontSize: 10,
          }}>
          Intel
        </button>
        {onOpenExplorer && (
          <button
            onClick={onOpenExplorer}
            title="3D Explorer — walk the block in three dimensions"
            style={{
              ...glassBtn(pillBg, pillBorder, accent, isDark),
              background: `${accent}22`,
              border: `1px solid ${accent}`,
              fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
              fontSize: 10,
              color: accent,
            }}>
            3D Explore
          </button>
        )}
        <button
          onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
          style={glassBtn(pillBg, pillBorder, fg, isDark)}
          title={isDark ? 'Switch to light map' : 'Switch to dark map'}>
          {isDark ? 'Light' : 'Dark'}
        </button>
        <button
          onClick={onExitToBoard}
          style={glassBtn(pillBg, pillBorder, fg, isDark)}>
          Board →
        </button>
        <button
          onClick={onExitToDashboard}
          style={glassBtn(pillBg, pillBorder, fg, isDark)}>
          Dashboard →
        </button>
      </div>

      {/* Top-center search box — sits below the toolbar row so the
          right-edge buttons can't overlap it on narrower viewports. */}
      <div style={{
        position: 'absolute', top: 72, left: '50%',
        transform: 'translateX(-50%)', zIndex: 5,
        width: 360, maxWidth: 'calc(100vw - 80px)',
      }}>
        <div style={{
          ...glassPill(pillBg, pillBorder, isDark),
          padding: '0 6px 0 14px', height: 38, gap: 8,
        }}>
          <span style={{
            color: fgMute, fontSize: 14, fontWeight: 600, lineHeight: 1,
          }}>⌕</span>
          <input
            value={searchQuery}
            onChange={async (e) => {
              const q = e.target.value;
              setSearchQuery(q);
              const qLower = q.trim().toLowerCase();
              if (qLower.length < 2) {
                setSearchResults([]);
                return;
              }

              // 1. Local: tracked properties (exact + substring match)
              const propMatches = properties.filter(p =>
                p.name.toLowerCase().includes(qLower) ||
                (p.location || '').toLowerCase().includes(qLower)
              ).slice(0, 4).map(p => ({
                label: p.name,
                sub:   p.location || 'Tracked property',
                kind:  'property' as const,
                lat:   p.lat,
                lng:   p.lng,
                zoom:  16,
              }));

              // 2. Local: focused MSAs
              const msaMatches = msaList.filter(m =>
                `${m.name}, ${m.state}`.toLowerCase().includes(qLower)
              ).slice(0, 3).map(m => ({
                label: `${m.name}, ${m.state}`,
                sub:   m.population
                  ? `pop. ${m.population.toLocaleString()}`
                  : 'Focused MSA',
                kind:  'msa' as const,
                lat:   m.lat,
                lng:   m.lng,
                zoom:  9.5,
              }));

              // Show local matches immediately; fetch Nominatim async
              const localResults = [...propMatches, ...msaMatches];
              setSearchResults(localResults);

              if (qLower.length < 3) return;
              setSearching(true);
              try {
                const r = await fetch(
                  `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=5&countrycodes=us&addressdetails=1&dedupe=1`,
                  { headers: { 'User-Agent': 'CoastTerminal/1.0' } },
                );
                const j = await r.json() as Array<{
                  display_name: string; lat: string; lon: string;
                  type?: string; class?: string;
                  address?: Record<string, string>;
                }>;
                const addressResults = j.map(item => {
                  // House-level results get tight zoom; broader places get less
                  const houseLevel = item.address?.house_number
                                  || item.class === 'building';
                  return {
                    label: item.display_name.split(',').slice(0, 2).join(','),
                    sub:   item.display_name.split(',').slice(2).join(',').trim()
                          || item.type || 'Address',
                    kind:  'address' as const,
                    lat:   parseFloat(item.lat),
                    lng:   parseFloat(item.lon),
                    zoom:  houseLevel ? 17 : 13,
                  };
                });
                // Property + MSA matches always first; address matches after
                setSearchResults([...localResults, ...addressResults]);
              } catch { /* keep local results */ }
              finally { setSearching(false); }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchResults[0]) {
                const r = searchResults[0];
                map.current?.flyTo({
                  center: [r.lng, r.lat],
                  zoom: r.zoom || 13,
                  speed: 1.0, curve: 1.4, essential: true,
                });
                if (r.kind === 'property') {
                  setSelected({ kind: 'property', name: r.label });
                } else if (r.kind === 'msa') {
                  const msa = msaList.find(m => `${m.name}, ${m.state}` === r.label);
                  if (msa) setSelected({ kind: 'msa', cbsa: msa.cbsa });
                }
                setSearchResults([]);
                setSearchQuery(r.label);
              }
            }}
            placeholder="Search property, MSA, or address…"
            style={{
              flex: 1, background: 'transparent', border: 'none',
              outline: 'none', color: fg, fontFamily: 'inherit',
              fontSize: 12, fontWeight: 500,
            }} />
          {searchQuery && (
            <button onClick={() => { setSearchQuery(''); setSearchResults([]); }}
              style={{
                background: 'transparent', border: 'none',
                color: fgMute, fontSize: 16, cursor: 'pointer',
                padding: '0 6px', fontFamily: 'inherit',
              }}>×</button>
          )}
        </div>
        {/* Autocomplete dropdown */}
        {searchResults.length > 0 && (
          <div style={{
            marginTop: 6,
            ...glassPanel(pillBg, pillBorder, isDark),
            padding: 6, maxHeight: 320, overflowY: 'auto',
          }}>
            {searchResults.map((r, i) => (
              <button key={i}
                onClick={() => {
                  map.current?.flyTo({
                    center: [r.lng, r.lat],
                    zoom: r.zoom || 13,
                    speed: 1.0, curve: 1.4, essential: true,
                  });
                  if (r.kind === 'property') {
                    setSelected({ kind: 'property', name: r.label });
                  } else if (r.kind === 'msa') {
                    const msa = msaList.find(m => `${m.name}, ${m.state}` === r.label);
                    if (msa) setSelected({ kind: 'msa', cbsa: msa.cbsa });
                  }
                  setSearchResults([]);
                  setSearchQuery(r.label);
                }}
                style={{
                  width: '100%', textAlign: 'left',
                  background: 'transparent', border: 'none',
                  color: fg, padding: '8px 10px',
                  cursor: 'pointer', borderRadius: 8,
                  fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                {/* Kind badge */}
                <span style={{
                  width: 56,
                  fontSize: 8, fontWeight: 700,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: r.kind === 'property' ? accent
                       : r.kind === 'msa'      ? '#1A7A4A'
                       : fgMute,
                  flexShrink: 0,
                }}>
                  {r.kind === 'property' ? 'Property'
                 : r.kind === 'msa'      ? 'MSA'
                 :                          'Address'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12, fontWeight: 600, color: fg,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {r.label}
                  </div>
                  {r.sub && (
                    <div style={{
                      fontSize: 10, color: fgMute, marginTop: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {r.sub}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
        {searching && searchResults.length === 0 && searchQuery.trim().length >= 3 && (
          <div style={{
            marginTop: 6,
            ...glassPanel(pillBg, pillBorder, isDark),
            padding: '8px 14px', fontSize: 11, color: fgMute,
          }}>
            Searching…
          </div>
        )}
      </div>

      {/* Top-right layer selector + legend */}
      <div style={{
        position: 'absolute', top: 18, right: 60,
        display: 'flex', flexDirection: 'column', gap: 8,
        alignItems: 'flex-end',
      }}>
        {/* Layer pill — click to open */}
        <button
          onClick={() => setLayerOpen(o => !o)}
          style={{
            ...glassPill(pillBg, pillBorder, isDark),
            padding: '8px 14px',
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
          <div>
            <div style={{
              color: accent, fontSize: 9, fontWeight: 700,
              letterSpacing: '0.18em', textTransform: 'uppercase',
            }}>
              Color by
            </div>
            <div style={{
              color: fg, fontSize: 12, fontWeight: 600,
              fontFamily: 'Fraunces, Georgia, serif',
            }}>
              {MAP_LAYERS.find(l => l.key === activeLayer)?.label}
            </div>
          </div>
          <span style={{ color: fgMute, fontSize: 11,
            transform: layerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s', display: 'inline-block',
          }}>▾</span>
        </button>

        {/* Layer dropdown */}
        {layerOpen && (
          <div style={{
            ...glassPanel(pillBg, pillBorder, isDark),
            padding: 6, minWidth: 260,
            display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            {/* Heatmap toggle */}
            <button
              onClick={() => setHeatmapOn(o => !o)}
              style={{
                background: 'transparent', border: 'none', textAlign: 'left',
                padding: '8px 12px', cursor: 'pointer', borderRadius: 8,
                fontFamily: 'inherit', color: fg,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
              {/* Toggle switch */}
              <div style={{
                width: 30, height: 18, borderRadius: 9,
                background: heatmapOn ? accent : (isDark ? '#3A3A45' : '#D1D5DB'),
                position: 'relative',
                transition: 'background 0.18s',
                flexShrink: 0,
              }}>
                <div style={{
                  position: 'absolute', top: 2,
                  left: heatmapOn ? 14 : 2,
                  width: 14, height: 14, borderRadius: 7,
                  background: '#FFFFFF',
                  transition: 'left 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: 13, fontWeight: 700, color: fg,
                }}>
                  Heatmap overlay
                </div>
                <div style={{ fontSize: 10, color: fgMute, marginTop: 1 }}>
                  Color the whole region by the selected metric
                </div>
              </div>
            </button>
            <div style={{
              height: 1, background: pillBorder, margin: '4px 8px',
            }} />
            {MAP_LAYERS.map(l => {
              const active = activeLayer === l.key;
              return (
                <button key={l.key}
                  onClick={() => { setActiveLayer(l.key); setLayerOpen(false); }}
                  style={{
                    background: active
                      ? (isDark ? 'rgba(201,169,110,0.18)' : 'rgba(201,169,110,0.14)')
                      : 'transparent',
                    border: 'none', textAlign: 'left',
                    padding: '8px 12px', cursor: 'pointer', borderRadius: 8,
                    fontFamily: 'inherit', color: fg,
                  }}
                  onMouseEnter={e => {
                    if (!active) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
                  }}
                  onMouseLeave={e => {
                    if (!active) e.currentTarget.style.background = 'transparent';
                  }}>
                  <div style={{
                    fontFamily: 'Fraunces, Georgia, serif',
                    fontSize: 13, fontWeight: active ? 700 : 600,
                    color: active ? accent : fg,
                  }}>{l.label}</div>
                  <div style={{ fontSize: 10, color: fgMute, marginTop: 1 }}>
                    {l.sublabel}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Heatmap legend strip */}
        {(() => {
          const layer = MAP_LAYERS.find(l => l.key === activeLayer);
          if (!layer || !marketData) return null;
          const vals: number[] = [];
          for (const mk of marketData.markets) {
            const v = valueForLayer(mk.metrics, layer, selectedYear ?? undefined);
            if (v !== null && v !== undefined && Number.isFinite(v)) vals.push(v);
          }
          if (vals.length === 0) return null;
          const lo = Math.min(...vals), hi = Math.max(...vals);
          const fmt = (v: number) => {
            if (layer.unit === 'pct')     return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
            if (layer.unit === 'money')   return `$${Math.round(v).toLocaleString()}`;
            if (layer.unit === 'persons') {
              if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
              if (v >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
              return Math.round(v).toLocaleString();
            }
            return Math.round(v).toLocaleString();
          };
          return (
            <div style={{
              ...glassPill(pillBg, pillBorder, isDark),
              padding: '8px 14px',
              flexDirection: 'column', alignItems: 'flex-end', gap: 6,
            }}>
              <div style={{
                color: fgMute, fontSize: 9, fontWeight: 700,
                letterSpacing: '0.12em', textTransform: 'uppercase',
              }}>
                {layer.inverse ? 'Lower = better' : 'Higher = better'}
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ color: fgMute, fontSize: 10, fontWeight: 600 }}>
                  {fmt(lo)}
                </span>
                <div style={{
                  width: 90, height: 6, borderRadius: 3,
                  background: layer.inverse
                    ? 'linear-gradient(90deg, #1A7A4A, #C9A96E, #CC2200)'
                    : 'linear-gradient(90deg, #CC2200, #C9A96E, #1A7A4A)',
                }} />
                <span style={{ color: fgMute, fontSize: 10, fontWeight: 600 }}>
                  {fmt(hi)}
                </span>
              </div>
            </div>
          );
        })()}

        {/* Legend pill */}
        <div style={{
          ...glassPill(pillBg, pillBorder, isDark),
          padding: '8px 14px',
          flexDirection: 'column' as const,
          alignItems: 'flex-start' as const,
          gap: 4,
        }}>
          <div style={{
            fontSize: 9, fontWeight: 700, color: accent,
            letterSpacing: '0.18em', textTransform: 'uppercase',
          }}>
            On the map
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 11, color: fg, fontWeight: 500 }}>
            <span style={{
              width: 12, height: 12, borderRadius: 6,
              background: `radial-gradient(circle, ${accent}, ${accent}55)`,
              boxShadow: `0 0 6px ${accent}88`, display: 'inline-block',
            }} />
            <span>{msaList.length} focused MSAs</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 11, color: fg, fontWeight: 500 }}>
            <span style={{
              width: 10, height: 10, borderRadius: 5,
              background: accent, display: 'inline-block',
              border: `2px solid ${isDark ? '#0F0F12' : '#fff'}`,
            }} />
            <span>{properties.length} tracked properties</span>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {(msaLoading || propsLoading) && msaList.length === 0 && properties.length === 0 && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          ...glassPill(pillBg, pillBorder, isDark),
          padding: '12px 22px', flexDirection: 'column',
          color: fgMute, fontSize: 12, textAlign: 'center',
          gap: 4,
        }}>
          <div style={{ color: accent, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            Loading
          </div>
          <div>focused MSAs and property pins…</div>
        </div>
      )}

      {/* Empty state (everything loaded, but nothing to plot) */}
      {!msaLoading && !propsLoading && msaList.length === 0 && properties.length === 0 && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          ...glassPill(pillBg, pillBorder, isDark),
          padding: '14px 22px', flexDirection: 'column',
          color: fg, fontSize: 13, textAlign: 'center', gap: 4,
          maxWidth: 360,
        }}>
          <div style={{ color: accent, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            No data yet
          </div>
          <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, fontWeight: 700 }}>
            Map is empty
          </div>
          <div style={{ color: fgMute, fontSize: 11, lineHeight: 1.5 }}>
            Set a focus in the Library or connect a property sheet to drop
            pins on the map.
          </div>
        </div>
      )}

      {/* Time slider — defaults to a compact bottom-LEFT pill so it
          stays out of the central view. Click to expand into the full
          scrubber. Playback forces expansion so the user can see
          movement; the X button collapses it back. */}
      {(() => {
        if (!marketData) return null;
        const layer = MAP_LAYERS.find(l => l.key === activeLayer) || MAP_LAYERS[0];
        const years = availableYears(marketData.markets, layer);
        if (years.length < 2) return null;
        const latestYear = years[years.length - 1];
        const yearShown = selectedYear ?? latestYear;
        const isLive = selectedYear === null || selectedYear === latestYear;
        const expanded = sliderExpanded || playing;

        const togglePlay = () => {
          if (!playing && (selectedYear === null || selectedYear === latestYear)) {
            setSelectedYear(years[0]);
          }
          setPlaying(p => !p);
        };

        return (
          <div style={{
            position: 'absolute',
            // Tuck into the bottom-left corner. When a bottom panel is open,
            // float just above it so neither blocks the other.
            bottom: (selectedMsa || selectedProp) ? 250 : 24,
            left: 18,
            transition: 'bottom 0.32s cubic-bezier(0.16, 1, 0.3, 1)',
            zIndex: 4,
          }}>
            {!expanded ? (
              /* ── Collapsed pill ─────────────────────────────────── */
              <div style={{
                ...glassPill(pillBg, pillBorder, isDark),
                padding: '6px 6px 6px 10px',
                gap: 10, cursor: 'pointer',
                opacity: 0.9,
                transition: 'opacity 0.2s, transform 0.18s',
              }}
                onClick={() => setSliderExpanded(true)}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.opacity = '1';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.opacity = '0.9';
                }}
                title="Show time slider">
                <div style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: 14, fontWeight: 700, color: fg,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                }}>
                  {yearShown}
                </div>
                <span style={{
                  color: fgMute, fontSize: 10, fontWeight: 500,
                  letterSpacing: '0.02em',
                }}>
                  {layer.label}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                  title="Play through years"
                  style={{
                    width: 24, height: 24, borderRadius: 12,
                    background: `${accent}22`,
                    border: `1px solid ${accent}88`,
                    color: accent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', padding: 0, marginLeft: 2,
                  }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 5v14l12-7z" />
                  </svg>
                </button>
              </div>
            ) : (
              /* ── Expanded panel ─────────────────────────────────── */
              <div style={{
                ...glassPanel(pillBg, pillBorder, isDark),
                padding: '10px 14px 12px',
                display: 'flex', flexDirection: 'column',
                gap: 8, width: 380, maxWidth: 'calc(100vw - 36px)',
                animation: 'coastFadeIn 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <button
                    onClick={togglePlay}
                    title={playing ? 'Pause autoplay' : 'Play through years'}
                    style={{
                      width: 30, height: 30, borderRadius: 15,
                      background: playing ? accent : `${accent}22`,
                      border: `1.5px solid ${accent}`,
                      color: playing ? '#FFFFFF' : accent,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', padding: 0, flex: '0 0 auto',
                      transition: 'all 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
                      boxShadow: playing
                        ? `0 0 0 4px ${accent}33, 0 0 16px ${accent}55`
                        : 'none',
                      animation: playing ? 'coastPulseSlow 1.8s infinite ease-in-out' : 'none',
                    }}>
                    {playing ? (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="6" y="5" width="4" height="14" rx="1" />
                        <rect x="14" y="5" width="4" height="14" rx="1" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 5v14l12-7z" />
                      </svg>
                    )}
                  </button>
                  <div style={{
                    fontFamily: 'Fraunces, Georgia, serif',
                    fontSize: 20, fontWeight: 800, color: fg,
                    lineHeight: 1, fontVariantNumeric: 'tabular-nums',
                  }}>
                    {yearShown}
                  </div>
                  <div style={{
                    color: fgMute, fontSize: 10, fontWeight: 500,
                    letterSpacing: '0.04em',
                  }}>
                    {layer.label}
                  </div>
                  <span style={{ flex: 1 }} />
                  {!isLive && (
                    <button
                      onClick={() => { setSelectedYear(null); setPlaying(false); }}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${pillBorder}`,
                        borderRadius: 7, padding: '2px 7px',
                        color: fgMute, fontSize: 9, fontWeight: 700,
                        letterSpacing: '0.1em', textTransform: 'uppercase',
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}>
                      Live
                    </button>
                  )}
                  <button
                    onClick={() => { setSliderExpanded(false); setPlaying(false); }}
                    title="Hide"
                    style={{
                      background: 'transparent', border: 'none',
                      color: fgMute, fontSize: 14, cursor: 'pointer',
                      padding: '0 4px', lineHeight: 1,
                      fontFamily: 'inherit',
                    }}>×</button>
                </div>

                <div style={{ padding: '2px 4px 0' }}>
                  <input
                    type="range"
                    min={years[0]}
                    max={latestYear}
                    step={1}
                    value={yearShown}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setPlaying(false);
                      setSelectedYear(v === latestYear ? null : v);
                    }}
                    style={{
                      width: '100%',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      height: 22,
                    }}
                    className="coast-time-slider"
                  />
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '2px 2px 0', userSelect: 'none',
                  }}>
                    <span style={{
                      color: fgMute, fontSize: 9,
                      fontVariantNumeric: 'tabular-nums',
                    }}>{years[0]}</span>
                    <span style={{
                      color: fgMute, fontSize: 9,
                      fontVariantNumeric: 'tabular-nums',
                    }}>{latestYear}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Top movers — bottom-right counterpart to the time slider. Ranks
          the focused MSAs by the active layer's value at the currently
          selected year and lets the user fly to any of them. */}
      {(() => {
        if (!marketData || msaList.length === 0) return null;
        const layer = MAP_LAYERS.find(l => l.key === activeLayer) || MAP_LAYERS[0];

        // Build a ranked list. Skip MSAs with no factual data for the year.
        const rows = msaList.map(ms => {
          const market = marketData.markets.find(x => x.cbsa === ms.cbsa);
          if (!market) return null;
          const metric = market.metrics[layer.metric];
          if (!metric) return null;
          const v = valueForLayer(market.metrics, layer, selectedYear ?? undefined);
          if (v === null || v === undefined || !Number.isFinite(v)) return null;
          // Compute YoY against the previous year in the series so the
          // mover delta stays accurate while the user scrubs back in time.
          let yoy: number | null = null;
          const series = metric.series || [];
          const ptYear = selectedYear ?? metric.year;
          const idx = series.findIndex(s => s.year === ptYear);
          if (idx > 0) {
            const prev = series[idx - 1].value;
            const cur  = series[idx].value;
            if (prev && prev !== 0) yoy = ((cur - prev) / Math.abs(prev)) * 100;
          }
          return {
            cbsa: ms.cbsa, name: ms.name, state: ms.state,
            lat: ms.lat, lng: ms.lng,
            value: v, yoy, series,
          };
        }).filter((r): r is NonNullable<typeof r> => r !== null);

        if (rows.length === 0) return null;

        rows.sort((a, b) => layer.inverse ? a.value - b.value : b.value - a.value);
        const top = rows.slice(0, 5);
        const yearShown = selectedYear ?? (() => {
          const yrs = availableYears(marketData.markets, layer);
          return yrs[yrs.length - 1];
        })();

        const fmtVal = (v: number) => {
          if (layer.unit === 'pct') return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
          if (layer.unit === 'money') return `$${Math.round(v).toLocaleString()}`;
          if (layer.unit === 'persons') {
            if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
            if (v >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
            return Math.round(v).toLocaleString();
          }
          return Math.round(v).toLocaleString();
        };

        // Tiny inline sparkline for each row — same style as the
        // Trajectory chart but axis-less, 70×16.
        const renderMiniSpark = (series: Array<{ year: number; value: number }>) => {
          if (!series || series.length < 2) return null;
          const w = 70, h = 16, pad = 1;
          const xs = series.map(s => s.year);
          const ys = series.map(s => s.value);
          const minX = Math.min(...xs), maxX = Math.max(...xs);
          const minY = Math.min(...ys), maxY = Math.max(...ys);
          const rY = (maxY - minY) || 1;
          const toX = (y: number) => pad + ((y - minX) / Math.max(1, maxX - minX)) * (w - 2 * pad);
          const toY = (v: number) => h - pad - ((v - minY) / rY) * (h - 2 * pad);
          const d = series.map((s, i) =>
            `${i === 0 ? 'M' : 'L'}${toX(s.year).toFixed(1)},${toY(s.value).toFixed(1)}`,
          ).join(' ');
          const last = series.find(s => s.year === yearShown) || series[series.length - 1];
          return (
            <svg width={w} height={h} style={{ display: 'block', flex: '0 0 auto' }}>
              <path d={d} fill="none" stroke={accent} strokeWidth={1.25}
                strokeLinejoin="round" strokeLinecap="round" />
              <circle cx={toX(last.year)} cy={toY(last.value)} r={2}
                fill={accent}
                stroke={isDark ? '#0a1220' : '#FFFFFF'} strokeWidth={1} />
            </svg>
          );
        };

        return (
          <div style={{
            position: 'absolute',
            bottom: (selectedMsa || selectedProp) ? 250 : 24,
            right: 18,
            transition: 'bottom 0.32s cubic-bezier(0.16, 1, 0.3, 1)',
            zIndex: 4,
          }}>
            {!moversOpen ? (
              <div
                onClick={() => setMoversOpen(true)}
                title="Top movers on the active layer"
                style={{
                  ...glassPill(pillBg, pillBorder, isDark),
                  padding: '6px 12px',
                  gap: 8, cursor: 'pointer', opacity: 0.9,
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.opacity = '1'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.opacity = '0.9'; }}>
                <span style={{
                  color: accent, fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.16em', textTransform: 'uppercase',
                }}>
                  Top {Math.min(top.length, 5)}
                </span>
                <span style={{ color: fgMute, fontSize: 11 }}>·</span>
                <span style={{ color: fg, fontSize: 11, fontWeight: 500 }}>
                  {top[0].name}
                </span>
                <span style={{
                  color: fgMute, fontSize: 10, fontVariantNumeric: 'tabular-nums',
                }}>
                  {fmtVal(top[0].value)}
                </span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                  stroke={fgMute} strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round"
                  style={{ marginLeft: 2 }}>
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </div>
            ) : (
              <div style={{
                ...glassPanel(pillBg, pillBorder, isDark),
                padding: '10px 12px 12px',
                width: 320, maxWidth: 'calc(100vw - 36px)',
                animation: 'coastFadeIn 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: 8,
                  marginBottom: 8,
                }}>
                  <div style={{
                    color: accent, fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.18em', textTransform: 'uppercase',
                  }}>
                    Top movers
                  </div>
                  <div style={{ color: fg, fontSize: 10, fontWeight: 500 }}>
                    {layer.label}
                  </div>
                  <span style={{ flex: 1 }} />
                  <div style={{
                    color: fgMute, fontSize: 10,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {yearShown}
                  </div>
                  <button
                    onClick={() => setMoversOpen(false)}
                    title="Hide"
                    style={{
                      background: 'transparent', border: 'none',
                      color: fgMute, fontSize: 14, cursor: 'pointer',
                      padding: '0 4px', lineHeight: 1,
                      fontFamily: 'inherit',
                    }}>×</button>
                </div>
                {top.map((r, i) => (
                  <div key={r.cbsa}
                    onClick={() => {
                      setSelected({ kind: 'msa', cbsa: r.cbsa });
                      map.current?.flyTo({
                        center: [r.lng, r.lat],
                        zoom: 9.5, speed: 1.0, curve: 1.4, essential: true,
                      });
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 4px', borderRadius: 6,
                      cursor: 'pointer',
                      borderTop: i === 0 ? 'none' : `1px solid ${pillBorder}`,
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLDivElement).style.backgroundColor =
                        isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                    }}>
                    <div style={{
                      width: 16, color: fgMute, fontSize: 10,
                      fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '0.05em',
                    }}>{i + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        color: fg, fontSize: 12, fontWeight: 500,
                        whiteSpace: 'nowrap', overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {r.name}
                      </div>
                      <div style={{
                        color: fgMute, fontSize: 9, fontWeight: 500,
                        marginTop: 1,
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {fmtVal(r.value)}
                        {r.yoy !== null && (
                          <span style={{
                            marginLeft: 6,
                            color: r.yoy > 0 ? '#1A7A4A' : r.yoy < 0 ? '#CC2200' : fgMute,
                            fontWeight: 700,
                          }}>
                            {r.yoy > 0 ? '+' : ''}{r.yoy.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                    {renderMiniSpark(r.series)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ─── Property Intelligence side panel ──────────────────────────
          Slides in from the right when Intel mode is active. Displays the
          anchored property, the Lifestyle Completion Score, per-category
          coverage bars, the AI-written neighbourhood narrative, and a
          category filter that scopes the map's POI canvas. */}
      {intelOpen && (
        <div style={{
          position: 'absolute',
          top: 70, right: 18, bottom: 18,
          width: 380, maxWidth: 'calc(100vw - 36px)',
          zIndex: 6,
          animation: 'coastFadeInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          <div style={{
            ...glassPanel(pillBg, pillBorder, isDark),
            height: '100%',
            padding: '18px 20px 18px',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: 8,
              marginBottom: 4,
            }}>
              <div style={{
                color: accent, fontSize: 9, fontWeight: 700,
                letterSpacing: '0.24em', textTransform: 'uppercase',
              }}>
                Property Intelligence
              </div>
              <span style={{ flex: 1 }} />
              <button
                onClick={() => {
                  intelRefreshRef.current = true;
                  setIntelData(null);
                  setIntelError(null);
                }}
                title="Refresh — force a fresh look from the latest sheet data"
                style={{
                  background: 'transparent', border: 'none',
                  color: fgMute, fontSize: 13, cursor: 'pointer',
                  padding: '0 6px', lineHeight: 1, fontFamily: 'inherit',
                }}>↻</button>
              <button
                onClick={() => {
                  setIntelOpen(false);
                  setIntelCategoryFilter(null);
                  // Drop cached bundle so re-opening pulls a fresh
                  // resolution from the user's current sheet data.
                  setIntelData(null);
                }}
                title="Exit Intel"
                style={{
                  background: 'transparent', border: 'none',
                  color: fgMute, fontSize: 16, cursor: 'pointer',
                  padding: 0, lineHeight: 1, fontFamily: 'inherit',
                }}>×</button>
            </div>

            {/* ── Address search ────────────────────────────────────────
                Lets the user pull intel for any address, not just the
                Parker default. Submitting (Enter / Search button) sets
                intelSearchAddress, which triggers the fetch effect above
                against /intel/location?address=... */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const v = intelSearchInput.trim();
                if (!v) return;
                setIntelSearchAddress(v);
                setIntelData(null);
                setIntelError(null);
              }}
              style={{
                display: 'flex', gap: 6, marginTop: 10, marginBottom: 4,
              }}
            >
              <input
                type="text"
                value={intelSearchInput}
                onChange={(e) => setIntelSearchInput(e.target.value)}
                placeholder="Search any address…"
                style={{
                  flex: 1,
                  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                  border: `1px solid ${pillBorder}`,
                  borderRadius: 6,
                  padding: '7px 10px',
                  fontSize: 12, color: fg,
                  fontFamily: 'inherit', outline: 'none',
                }}
              />
              <button
                type="submit"
                disabled={!intelSearchInput.trim() || intelLoading}
                style={{
                  background: intelSearchInput.trim() ? accent : 'transparent',
                  border: `1px solid ${intelSearchInput.trim() ? accent : pillBorder}`,
                  color: intelSearchInput.trim() ? '#0a1322' : fgMute,
                  padding: '7px 14px', fontSize: 11, fontWeight: 600,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  cursor: intelSearchInput.trim() ? 'pointer' : 'default',
                  fontFamily: 'inherit', borderRadius: 6,
                  opacity: (intelSearchInput.trim() && !intelLoading) ? 1 : 0.55,
                }}
              >
                Go
              </button>
              {intelSearchAddress && (
                <button
                  type="button"
                  onClick={() => {
                    setIntelSearchAddress(null);
                    setIntelSearchInput('');
                    setIntelData(null);
                    setIntelError(null);
                  }}
                  title="Reset to The Parker default"
                  style={{
                    background: 'transparent',
                    border: `1px solid ${pillBorder}`,
                    color: fgMute,
                    padding: '7px 10px', fontSize: 11,
                    cursor: 'pointer', fontFamily: 'inherit',
                    borderRadius: 6,
                  }}
                >
                  Reset
                </button>
              )}
            </form>

            {intelLoading && !intelData && (
              <div style={{
                marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                {/* Concrete progress card — replaces the vague "Reading the
                    neighbourhood…" string. Tells the user EXACTLY which
                    address is being processed and what step we're on, so
                    a 20-30s wait reads as work-in-progress rather than
                    "did my click do anything?" */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px',
                  background: isDark ? 'rgba(201,169,110,0.08)' : 'rgba(184,149,106,0.10)',
                  border: `1px solid ${accent}55`, borderRadius: 8,
                }}>
                  <div style={{
                    width: 14, height: 14, flexShrink: 0,
                    border: `2px solid ${accent}44`,
                    borderTopColor: accent,
                    borderRadius: '50%',
                    animation: 'coastSpinIntel 0.9s linear infinite',
                  }} />
                  <div style={{ flex: 1, lineHeight: 1.4 }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: fg,
                      letterSpacing: '0.04em',
                    }}>
                      Looking up{intelSearchAddress ? ` ${intelSearchAddress}` : '…'}
                    </div>
                    <div style={{
                      fontSize: 10, color: fgMute, marginTop: 2,
                      fontStyle: 'italic',
                    }}>
                      Geocoding · pulling POIs from OpenStreetMap · scoring lifestyle · generating narrative.
                    </div>
                    <div style={{
                      fontSize: 9, color: fgMute, marginTop: 6,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}>
                      Typically 20–40s on first request.
                    </div>
                  </div>
                </div>
                <style>{`
                  @keyframes coastSpinIntel { to { transform: rotate(360deg); } }
                `}</style>
              </div>
            )}
            {intelError && (
              <div style={{
                color: '#CC2200', fontSize: 11, marginTop: 12, lineHeight: 1.5,
              }}>
                {intelError}
                <button
                  onClick={() => { setIntelData(null); setIntelError(null); }}
                  style={{
                    marginLeft: 8, background: 'transparent',
                    border: `1px solid ${pillBorder}`, borderRadius: 6,
                    padding: '2px 8px', cursor: 'pointer',
                    color: fg, fontSize: 10, fontFamily: 'inherit',
                  }}>Retry</button>
              </div>
            )}

            {intelData && (
              <div style={{
                flex: 1, overflowY: 'auto', marginRight: -10, paddingRight: 10,
              }}>
                {/* Property identity */}
                <div style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: 22, fontWeight: 800, color: fg, lineHeight: 1.1,
                }}>
                  {intelData.property.name}
                </div>
                <div style={{
                  color: fgMute, fontSize: 11, marginTop: 4, lineHeight: 1.45,
                }}>
                  {intelData.property.address}
                </div>
                {/* Only render the submarket + year-built strip when we
                    actually have those values. Searched-by-address
                    locations don't carry them — the strip would otherwise
                    render "null · submarket / built null". */}
                {(intelData.property.submarket || intelData.property.year_built) && (
                  <div style={{
                    display: 'flex', gap: 14, marginTop: 8,
                    fontSize: 10, color: fgMute, letterSpacing: '0.04em',
                  }}>
                    {intelData.property.submarket && (
                      <span><b style={{ color: fg, fontWeight: 600 }}>{intelData.property.submarket}</b> · submarket</span>
                    )}
                    {intelData.property.year_built && (
                      <span>built {intelData.property.year_built}</span>
                    )}
                  </div>
                )}

                {/* Lifestyle Completion Score */}
                <div style={{
                  marginTop: 18, padding: '14px 16px',
                  background: isDark ? 'rgba(201,169,110,0.10)' : 'rgba(201,169,110,0.08)',
                  border: `1px solid ${accent}55`,
                  borderRadius: 12,
                }}>
                  <div style={{
                    color: accent, fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.20em', textTransform: 'uppercase',
                  }}>
                    Lifestyle Completion
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'baseline', gap: 6,
                    marginTop: 4,
                  }}>
                    <div style={{
                      fontFamily: 'Fraunces, Georgia, serif',
                      fontSize: 38, fontWeight: 800, color: fg,
                      lineHeight: 1, fontVariantNumeric: 'tabular-nums',
                    }}>
                      {Math.round(intelData.lifestyle.score)}
                    </div>
                    <div style={{ color: fgMute, fontSize: 11, fontWeight: 600 }}>
                      / 100
                    </div>
                    <span style={{ flex: 1 }} />
                    <div style={{ color: fgMute, fontSize: 9, letterSpacing: '0.04em' }}>
                      across {intelData.pois.length} POIs in 15-min walk
                    </div>
                  </div>
                </div>

                {/* Category coverage rows */}
                <div style={{ marginTop: 16 }}>
                  <div style={{
                    color: accent, fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.20em', textTransform: 'uppercase',
                    marginBottom: 8,
                  }}>
                    Coverage by category
                  </div>
                  {intelData.lifestyle.categories.map(c => {
                    const active = intelCategoryFilter === c.key;
                    return (
                      <div key={c.key}
                        onClick={() => setIntelCategoryFilter(active ? null : c.key)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '92px 1fr 56px',
                          alignItems: 'center', gap: 10,
                          padding: '6px 6px', borderRadius: 6,
                          cursor: 'pointer',
                          background: active
                            ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')
                            : 'transparent',
                          transition: 'background-color 0.15s',
                        }}
                        title={`Click to ${active ? 'clear' : 'isolate'} ${c.label} on the map`}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          color: active ? fg : fgMute,
                          fontSize: 11,
                          fontWeight: active ? 700 : 500,
                        }}>
                          <span style={{
                            width: 8, height: 8, borderRadius: 4,
                            background: c.color, flex: '0 0 auto',
                            boxShadow: `0 0 6px ${c.color}66`,
                          }} />
                          {c.label}
                        </div>
                        <div style={{
                          position: 'relative', height: 6,
                          background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
                          borderRadius: 3, overflow: 'hidden',
                        }}>
                          <div style={{
                            position: 'absolute', inset: 0,
                            width: `${Math.min(c.coverage, 100)}%`,
                            background: c.color,
                            opacity: 0.85,
                            transition: 'width 0.4s ease',
                          }} />
                        </div>
                        <div style={{
                          color: fgMute, fontSize: 10, fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                        }}>
                          {c.count}/{c.target}
                        </div>
                      </div>
                    );
                  })}
                  {intelCategoryFilter && (
                    <button
                      onClick={() => setIntelCategoryFilter(null)}
                      style={{
                        marginTop: 6, background: 'transparent',
                        border: `1px solid ${pillBorder}`, borderRadius: 6,
                        padding: '2px 8px', cursor: 'pointer',
                        color: fgMute, fontSize: 9, fontFamily: 'inherit',
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                      }}>
                      Clear filter
                    </button>
                  )}
                </div>

                {/* Ring legend */}
                <div style={{ marginTop: 16 }}>
                  <div style={{
                    color: accent, fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.20em', textTransform: 'uppercase',
                    marginBottom: 6,
                  }}>
                    Human experience rings
                  </div>
                  {intelData.rings.map((r) => {
                    const counts = intelData.counts_by_ring[r.key as 'life' | 'social' | 'work'] || {};
                    const total = Object.values(counts).reduce((a, b) => a + b, 0);
                    return (
                      <div key={r.key} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: 11, color: fgMute, padding: '2px 4px',
                      }}>
                        <span style={{
                          width: 6, height: 6, borderRadius: 3,
                          background: accent, opacity: r.key === 'life' ? 1 : r.key === 'social' ? 0.65 : 0.4,
                        }} />
                        <span style={{ color: fg, fontWeight: 500, width: 90 }}>
                          {r.label}
                        </span>
                        <span style={{ flex: 1 }} />
                        <span style={{
                          fontVariantNumeric: 'tabular-nums',
                          color: fgMute,
                        }}>
                          {total} POIs
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Tenant Personas — Phase 3 capstone */}
                {intelData.personas && intelData.personas.length > 0 && (
                  <div style={{ marginTop: 18 }}>
                    <div style={{
                      color: accent, fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.20em', textTransform: 'uppercase',
                      marginBottom: 8,
                    }}>
                      Likely tenants
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {intelData.personas.slice(0, 3).map((p, i) => {
                        // Convert category keys to labels via the lookup we already have
                        const catLabel = (k: string) =>
                          intelData.categories.find(c => c.key === k)?.label || k;
                        return (
                          <div key={p.key} style={{
                            padding: '10px 12px',
                            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)',
                            border: `1px solid ${pillBorder}`,
                            borderLeft: `3px solid ${accent}`,
                            borderRadius: 10,
                          }}>
                            <div style={{
                              display: 'flex', alignItems: 'baseline', gap: 8,
                            }}>
                              <span style={{
                                color: accent, fontSize: 14, lineHeight: 1,
                              }}>{p.icon}</span>
                              <span style={{
                                color: fg, fontSize: 12, fontWeight: 600,
                              }}>{p.label}</span>
                              <span style={{ flex: 1 }} />
                              <span style={{
                                fontFamily: 'Fraunces, Georgia, serif',
                                fontSize: 18, fontWeight: 800,
                                color: i === 0 ? accent : fg,
                                fontVariantNumeric: 'tabular-nums',
                                lineHeight: 1,
                              }}>
                                {Math.round(p.fit)}
                              </span>
                              <span style={{
                                color: fgMute, fontSize: 9, fontWeight: 600,
                              }}>fit</span>
                            </div>
                            <div style={{
                              color: fgMute, fontSize: 10, marginTop: 4,
                              lineHeight: 1.4,
                            }}>
                              {p.tag}
                            </div>
                            <div style={{
                              display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6,
                            }}>
                              {p.wants.map(k => (
                                <span key={k} style={{
                                  fontSize: 9, color: fgMute,
                                  padding: '2px 7px', borderRadius: 100,
                                  background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                                  letterSpacing: '0.04em',
                                }}>
                                  {catLabel(k)}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Narrative */}
                {intelData.narrative && (
                  <div style={{ marginTop: 18 }}>
                    <div style={{
                      color: accent, fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.20em', textTransform: 'uppercase',
                      marginBottom: 6,
                    }}>
                      Spatial read
                    </div>
                    <div style={{
                      fontSize: 12, color: fg, lineHeight: 1.55,
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'EB Garamond, Georgia, serif',
                    }}>
                      {intelData.narrative}
                    </div>
                  </div>
                )}

                {/* CTA — walk the block in 3D */}
                {onOpenExplorer && (
                  <button
                    onClick={onOpenExplorer}
                    style={{
                      marginTop: 18, width: '100%',
                      padding: '12px 16px',
                      background: accent, color: '#FFFFFF',
                      border: `1px solid ${accent}`, borderRadius: 12,
                      fontWeight: 700, letterSpacing: '0.14em',
                      textTransform: 'uppercase', fontSize: 11,
                      fontFamily: 'inherit', cursor: 'pointer',
                      boxShadow: `0 0 22px ${accent}44`,
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: 10,
                      transition: 'transform 0.15s, box-shadow 0.15s',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                      (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 4px 28px ${accent}66`;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                      (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 22px ${accent}44`;
                    }}>
                    Walk this block in 3D
                    <span style={{ fontSize: 14, lineHeight: 1 }}>→</span>
                  </button>
                )}

                {/* Roadmap of what's coming */}
                <div style={{
                  marginTop: 20, padding: 12,
                  background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)',
                  borderRadius: 10,
                  border: `1px dashed ${pillBorder}`,
                }}>
                  <div style={{
                    color: fgMute, fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.20em', textTransform: 'uppercase',
                    marginBottom: 6,
                  }}>
                    Coming next
                  </div>
                  <div style={{
                    color: fgMute, fontSize: 10, lineHeight: 1.6,
                  }}>
                    Neighborhood energy layer · invisible competition · development pressure · street wealth perception · mental map of LA · time-of-day behavioural simulation
                  </div>
                </div>

                <div style={{
                  marginTop: 14, color: fgMute, fontSize: 9,
                  fontStyle: 'italic',
                }}>
                  Sources: OpenStreetMap (POIs), Claude Sonnet (narrative).
                  {intelData._cache ? ' Cached.' : ' Live.'} {intelData.elapsed_s}s.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom slide-up panel for selected entity */}
      {(selectedMsa || selectedProp) && (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          padding: '0 20px 20px',
          animation: 'coastSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
          pointerEvents: 'none',
        }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              ...glassPanel(pillBg, pillBorder, isDark),
              pointerEvents: 'auto',
              maxWidth: 640,
              margin: '0 auto',
              padding: '20px 24px',
            }}>
            {/* Drag handle line */}
            <div style={{
              width: 38, height: 4, borderRadius: 2,
              background: fgMute, opacity: 0.4,
              margin: '0 auto 14px',
            }} />

            {/* MSA panel */}
            {selectedMsa && selectedMsaGeo && marketData && (
              <div>
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: 10,
                  marginBottom: 4,
                }}>
                  <div style={{
                    color: accent, fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.22em', textTransform: 'uppercase',
                  }}>
                    Metro
                  </div>
                  {selectedMsaGeo.population && (
                    <div style={{
                      color: fgMute, fontSize: 10, fontWeight: 600,
                    }}>
                      pop. {selectedMsaGeo.population.toLocaleString()}
                    </div>
                  )}
                  <span style={{ flex: 1 }} />
                  <button onClick={() => setSelected(null)} style={{
                    background: 'transparent', border: 'none',
                    color: fgMute, fontSize: 12, cursor: 'pointer',
                    fontFamily: 'inherit', padding: 0,
                  }}>×</button>
                </div>
                <div style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: 22, fontWeight: 800, color: fg,
                  marginBottom: 14,
                }}>
                  {selectedMsa.name}, {selectedMsa.state}
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 14,
                }}>
                  {marketData.indicators.slice(0, 6).map(ind => {
                    const v = selectedMsa.metrics?.[ind.key];
                    if (!v || v.value === null) return null;

                    // Resolve the displayed value + YoY against the
                    // selected year. Falls back to latest when slider
                    // is set to live.
                    let displayValue = v.value;
                    let displayYoy   = v.yoy_pct;
                    let displayYear  = v.year;
                    if (selectedYear !== null && v.series && selectedYear !== v.year) {
                      const entry = v.series.find(s => s.year === selectedYear);
                      if (entry) {
                        displayValue = entry.value;
                        displayYear  = entry.year;
                        const prevIdx = v.series.findIndex(s => s.year === selectedYear) - 1;
                        const prev = prevIdx >= 0 ? v.series[prevIdx]?.value : null;
                        displayYoy = (prev && prev !== 0)
                          ? ((entry.value - prev) / prev) * 100
                          : null;
                      }
                    }
                    return (
                      <div key={ind.key} style={{
                        borderLeft: `2px solid ${accent}55`, paddingLeft: 12,
                      }}>
                        <div style={{
                          fontSize: 9, color: fgMute, fontWeight: 600,
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                          marginBottom: 2,
                        }}>
                          {ind.label}
                          <span style={{
                            color: accent, fontWeight: 700, marginLeft: 5,
                          }}>
                            {displayYear}
                          </span>
                        </div>
                        <div style={{
                          fontFamily: 'Fraunces, Georgia, serif',
                          fontSize: 18, fontWeight: 700, color: fg,
                          lineHeight: 1.15,
                        }}>
                          {fmtMetric(displayValue, ind.fmt)}
                        </div>
                        {displayYoy !== null && displayYoy !== undefined && (
                          <div style={{
                            fontSize: 10, fontWeight: 700,
                            color: displayYoy > 0 ? '#1A7A4A' : displayYoy < 0 ? '#CC2200' : fgMute,
                            marginTop: 2,
                          }}>
                            {displayYoy > 0 ? '+' : ''}{displayYoy.toFixed(1)}% YoY
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Trajectory chart — one bigger line for the active
                    layer's metric, only when there's enough real data.
                    No interpolation or synthetic points: every plotted
                    point comes from the database series. */}
                {(() => {
                  const layer = MAP_LAYERS.find(l => l.key === activeLayer) || MAP_LAYERS[0];
                  const ind = marketData.indicators.find(i => i.key === layer.metric);
                  const metric = selectedMsa.metrics?.[layer.metric];
                  if (!ind || !metric || !metric.series || metric.series.length < 3) return null;
                  const ptYear = (selectedYear ?? metric.year);
                  return (
                    <div style={{ marginTop: 18 }}>
                      <div style={{
                        display: 'flex', alignItems: 'baseline', gap: 8,
                        marginBottom: 8,
                      }}>
                        <div style={{
                          color: accent, fontSize: 9, fontWeight: 700,
                          letterSpacing: '0.18em', textTransform: 'uppercase',
                        }}>
                          Trajectory
                        </div>
                        <div style={{ color: fg, fontSize: 11, fontWeight: 500 }}>
                          {ind.label}
                        </div>
                        <span style={{ flex: 1 }} />
                        <div style={{ color: fgMute, fontSize: 10 }}>
                          {metric.series.length} years · {metric.source || 'source'}
                        </div>
                      </div>
                      <Trajectory
                        series={metric.series}
                        selectedYear={ptYear}
                        fmt={ind.fmt}
                        fg={fg}
                        fgMute={fgMute}
                        accent={accent}
                        isDark={isDark}
                      />
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Property panel */}
            {selectedProp && (
              <div>
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: 10,
                  marginBottom: 4,
                }}>
                  <div style={{
                    color: accent, fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.22em', textTransform: 'uppercase',
                  }}>
                    Property
                  </div>
                  <span style={{ flex: 1 }} />
                  <button onClick={() => setSelected(null)} style={{
                    background: 'transparent', border: 'none',
                    color: fgMute, fontSize: 12, cursor: 'pointer',
                    fontFamily: 'inherit', padding: 0,
                  }}>×</button>
                </div>
                <div style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: 22, fontWeight: 800, color: fg,
                  marginBottom: 4,
                }}>
                  {selectedProp.name}
                </div>
                {selectedProp.location && (
                  <div style={{
                    color: fgMute, fontSize: 11, marginBottom: 12,
                  }}>
                    {selectedProp.location}
                  </div>
                )}

                {/* Niche tags */}
                {selectedProp.leading && selectedProp.leading.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                    {selectedProp.leading.slice(0, 3).map((l, i) => (
                      <span key={i} style={{
                        background: accent + '22', color: accent,
                        fontSize: 9, fontWeight: 700,
                        letterSpacing: '0.14em', textTransform: 'uppercase',
                        padding: '3px 9px', borderRadius: 4,
                      }}>{l.label}</span>
                    ))}
                  </div>
                )}

                {/* Stats grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 14, marginBottom: 14,
                }}>
                  {selectedProp.price !== null && (
                    <Stat label={`Rent (${selectedProp.price_unit || 'typical'})`}
                      value={`$${Math.round(selectedProp.price).toLocaleString()}/mo`}
                      fg={fg} fgMute={fgMute} />
                  )}
                  {selectedProp.value !== null && (
                    <Stat label="$/sqft"
                      value={`$${selectedProp.value.toFixed(2)}`}
                      fg={fg} fgMute={fgMute} />
                  )}
                  {selectedProp.newness !== null && (
                    <Stat label="built"
                      value={String(Math.round(selectedProp.newness))}
                      fg={fg} fgMute={fgMute} />
                  )}
                  {selectedProp.amenity !== null && (
                    <Stat label="amenities"
                      value={String(Math.round(selectedProp.amenity))}
                      fg={fg} fgMute={fgMute} />
                  )}
                </div>

                {/* Amenity preview */}
                {(selectedProp.bld_sample?.length || selectedProp.unit_sample?.length) ? (
                  <div style={{
                    paddingTop: 12, borderTop: `1px solid ${pillBorder}`,
                    fontSize: 10, color: fgMute, lineHeight: 1.5,
                  }}>
                    {selectedProp.bld_sample && selectedProp.bld_sample.length > 0 && (
                      <div>
                        <span style={{ color: accent, fontWeight: 700,
                          letterSpacing: '0.08em' }}>BUILDING: </span>
                        <span style={{ color: fg }}>
                          {selectedProp.bld_sample.slice(0, 5).join(', ')}
                        </span>
                      </div>
                    )}
                    {selectedProp.unit_sample && selectedProp.unit_sample.length > 0 && (
                      <div style={{ marginTop: 3 }}>
                        <span style={{ color: accent, fontWeight: 700,
                          letterSpacing: '0.08em' }}>IN-UNIT: </span>
                        <span style={{ color: fg }}>
                          {selectedProp.unit_sample.slice(0, 5).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function Stat({ label, value, fg, fgMute }: {
  label: string; value: string; fg: string; fgMute: string;
}) {
  return (
    <div>
      <div style={{
        fontSize: 9, color: fgMute, fontWeight: 600,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        marginBottom: 2,
      }}>{label}</div>
      <div style={{
        fontFamily: 'Fraunces, Georgia, serif',
        fontSize: 15, fontWeight: 700, color: fg, lineHeight: 1.2,
      }}>{value}</div>
    </div>
  );
}

function glassPill(bg: string, border: string, isDark: boolean): React.CSSProperties {
  return {
    background: bg,
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: `1px solid ${border}`,
    borderRadius: 14,
    padding: '8px 14px',
    boxShadow: isDark
      ? '0 8px 32px rgba(0,0,0,0.5)'
      : '0 2px 12px rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'center',
  };
}

function glassBtn(bg: string, border: string, fg: string, isDark: boolean): React.CSSProperties {
  return {
    background: bg,
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: `1px solid ${border}`,
    borderRadius: 12,
    padding: '8px 14px',
    color: fg,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: isDark
      ? '0 4px 16px rgba(0,0,0,0.4)'
      : '0 2px 8px rgba(0,0,0,0.06)',
  };
}

function glassPanel(bg: string, border: string, isDark: boolean): React.CSSProperties {
  return {
    background: bg,
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    border: `1px solid ${border}`,
    borderRadius: 20,
    boxShadow: isDark
      ? '0 24px 64px rgba(0,0,0,0.6)'
      : '0 12px 48px rgba(0,0,0,0.12)',
  };
}

function firstSymbolId(map: maplibregl.Map): string | undefined {
  // Find the topmost non-symbol layer so we can insert beneath labels.
  // Returning a symbol layer id makes addLayer place the new layer below it.
  const layers = map.getStyle().layers || [];
  for (const l of layers) {
    if (l.type === 'symbol') return l.id;
  }
  return undefined;
}

function fmtMetric(value: number, fmt: string): string {
  if (fmt === 'int_compact') {
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
    return Math.round(value).toLocaleString();
  }
  if (fmt === 'money') {
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}k`;
    return `$${Math.round(value).toLocaleString()}`;
  }
  if (fmt === 'money_per_month') return `$${Math.round(value).toLocaleString()}/mo`;
  if (fmt === 'pct')     return `${value.toFixed(1)}%`;
  if (fmt === 'decimal') return value.toFixed(1);
  if (fmt === 'int' || fmt === 'int_signed') return Math.round(value).toLocaleString();
  return String(value);
}
